import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/api';
import { format, parseISO } from 'date-fns';
import { isPushSupported, pushPermissionState, getCurrentSubscription, enablePushNotifications } from '../lib/push';
import { isNativeApp, getNativePushPermission, registerNativePush } from '../lib/nativePush';

// Body parts in a sensible gym order; anything else is appended A–Z.
const BODY_PART_ORDER = ['Chest', 'Back', 'Shoulders', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core', 'Full Body', 'Cardio'];

// A distinct accent colour per body part — used for the exercise picker so
// it isn't a wall of grey. Anything unknown falls back to slate.
const PART_COLORS = {
  Chest: '#C62828', Back: '#1565C0', Shoulders: '#6A1B9A', Quads: '#2E7D32',
  Hamstrings: '#00838F', Glutes: '#AD1457', Calves: '#4E342E', Biceps: '#EF6C00',
  Triceps: '#F9A825', Core: '#283593', 'Full Body': '#37474F', Cardio: '#D84315',
};
const partColor = (p) => PART_COLORS[p] || '#546E7A';

// ── Styling helpers ───────────────────────────────────────────────────
const card = {
  background: '#fff', borderRadius: 14, boxShadow: 'var(--shadow)',
  padding: 16, marginBottom: 14,
};
const label = {
  fontSize: '0.62rem', fontWeight: 700, color: 'var(--grey-500)',
  letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6,
};
const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem',
};

const SET_LABELS = { 2: 'Superset', 3: 'Triset', 4: 'Giant set' };
function setTypeLabel(n) { return n <= 1 ? null : (SET_LABELS[n] || 'Giant set'); }

// Group exercises (already ordered) into super/tri-sets by the leading
// letter of their code: A1 + A2 share group "A".
function groupExercises(exercises) {
  const groups = [];
  let cur = null;
  (exercises || []).forEach(ex => {
    const m = (ex.code || '').match(/^[A-Za-z]+/);
    const key = m ? m[0].toUpperCase() : `solo-${ex.id}`;
    if (!cur || cur.key !== key) { cur = { key, items: [] }; groups.push(cur); }
    cur.items.push(ex);
  });
  return groups;
}

function restText(secs) {
  if (secs == null || secs === '') return null;
  const s = Number(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}

// ── In-progress workout drafts (saved on this device) ─────────────────
// Lets a member leave the screen mid-session (or get interrupted) and pick
// up exactly where they left off. The draft lives in the browser's local
// storage keyed by programme, and is cleared once the session is saved.
const draftKey = (programmeId) => `tb_training_draft_${programmeId}`;
function readDraft(programmeId) {
  try {
    const raw = window.localStorage.getItem(draftKey(programmeId));
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}
function writeDraft(programmeId, data) {
  try { window.localStorage.setItem(draftKey(programmeId), JSON.stringify(data)); } catch (_) {}
}
function clearDraft(programmeId) {
  try { window.localStorage.removeItem(draftKey(programmeId)); } catch (_) {}
}
function hasDraft(programmeId) {
  try { return !!window.localStorage.getItem(draftKey(programmeId)); } catch (_) { return false; }
}

// ── Exercise read-out (within a programme) ────────────────────────────
function ExerciseGroups({ exercises }) {
  const groups = groupExercises(exercises);
  return (
    <>
      {groups.map((g, gi) => {
        const tag = setTypeLabel(g.items.length);
        return (
          <div key={gi} style={{
            borderLeft: tag ? '3px solid var(--green)' : '3px solid transparent',
            paddingLeft: tag ? 10 : 0, marginBottom: 8,
          }}>
            {tag && <div style={{ fontSize: '0.6rem', fontWeight: 800, color: 'var(--green)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>{tag}</div>}
            {g.items.map(ex => (
              <div key={ex.id} style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {ex.code ? <span style={{ color: 'var(--grey-500)', marginRight: 6 }}>{ex.code}</span> : null}
                    {ex.name}
                  </span>
                  <span style={{ color: 'var(--grey-700)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {ex.sets ? `${ex.sets}×` : ''}{ex.reps || ''}
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {ex.tempo && <span>Tempo {ex.tempo}</span>}
                  {restText(ex.rest_seconds) && <span>Rest {restText(ex.rest_seconds)}</span>}
                  {ex.notes && <span style={{ fontStyle: 'italic' }}>{ex.notes}</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

// ── Log Session flow ──────────────────────────────────────────────────
// A fresh, empty set. Carries both strength fields (weight/reps) and cardio
// fields (duration/distance/resistance/calories) plus a per-set "how did it
// feel" note. Whichever pair is shown depends on the exercise's body part.
const blankSet = () => ({
  weight: '', reps: '', note: '',
  duration_min: '', distance: '', resistance: '', calories: '',
});

// ── Stretches & mobilisations ─────────────────────────────────────────
// Defined once per phase, shown before every session as a warm-up reminder.
// Members don't log completion. Each item is either a single amount or a
// Left/Right split (free text, so e.g. "45s", "10 reps", "3 breaths").
const blankMobility = () => ({ name: '', mode: 'single', amount: '', left: '', right: '' });

// Starter list of common stretches & mobilisations offered as a dropdown in
// the blue box. It's a datalist, so staff can still type anything custom.
const MOBILITY_SUGGESTIONS = [
  '90-90 hip stretch', "World's greatest stretch", 'Couch stretch', 'Hip flexor stretch',
  'Pigeon stretch', 'Glute stretch (figure-4)', 'Adductor rock back', 'Cossack squat',
  'Deep squat hold', 'Spiderman lunge', 'Hamstring stretch', 'Standing toe touch',
  'Calf stretch', 'Quad stretch', 'Ankle mobility (knee-to-wall)',
  'Thoracic rotation', 'Cat-cow', 'Thread the needle', 'Child\u2019s pose',
  'Cobra stretch', 'Scorpion stretch', 'Lat stretch', 'Doorway pec stretch',
  'Band shoulder dislocates', 'Wall slides', 'Neck stretch', 'Wrist mobility',
  'Downward dog', 'Seated forward fold',
];

// Read-only reminder block shown at the top of a session.
function MobilityView({ items }) {
  const list = (items || []).filter(m => m && (m.name || '').trim());
  if (list.length === 0) return null;
  return (
    <div style={{ background: '#EEF6FF', border: '1px solid #BBDCF8', borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ fontWeight: 800, fontSize: '0.78rem', color: '#1565C0', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 2 }}>
        🤸 Warm-up: stretches &amp; mobilisations
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--grey-500)', marginBottom: 8 }}>Do these before you start — no need to log them.</div>
      {list.map((m, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, padding: '5px 0', borderTop: i ? '1px solid #D7EBFB' : 'none' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--grey-800, #222)' }}>{m.name}</span>
          <span style={{ fontSize: '0.82rem', color: '#1565C0', fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'right' }}>
            {m.mode === 'split'
              ? <>{m.left ? `L ${m.left}` : ''}{m.left && m.right ? '  ·  ' : ''}{m.right ? `R ${m.right}` : ''}</>
              : (m.amount || '')}
          </span>
        </div>
      ))}
    </div>
  );
}

// Editor used in the phase builder (coach + member).
function MobilityEditor({ items, onChange, inProgramme, onInProgrammeChange }) {
  const list = items || [];
  const update = (i, patch) => onChange(list.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const add = () => onChange([...list, blankMobility()]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const small = { ...inputStyle, padding: '8px 10px', fontSize: '0.9rem' };

  return (
    <div style={{ background: '#EEF6FF', borderRadius: 10, padding: 12, marginBottom: 12, border: '1px solid #BBDCF8' }}>
      <datalist id="tb-mobility-suggestions">
        {MOBILITY_SUGGESTIONS.map(s => <option key={s} value={s} />)}
      </datalist>
      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1565C0', marginBottom: 2 }}>🤸 Stretching &amp; Mobility</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--grey-600, #555)', marginBottom: 10 }}>
        Add any stretches or mobilisations for this phase. Members tick each one off and can log a hold.
      </div>

      {list.map((m, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid #D7EBFB', borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input style={{ ...small, flex: 1 }} value={m.name} list="tb-mobility-suggestions"
              onChange={e => update(i, { name: e.target.value })} placeholder="Pick or type a stretch / mobilisation" />
            <button onClick={() => remove(i)} style={{ color: 'var(--danger)', fontSize: '1.1rem', lineHeight: 1 }} aria-label="Remove">×</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.78rem', color: 'var(--grey-700)', marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={m.mode === 'split'}
              onChange={e => update(i, { mode: e.target.checked ? 'split' : 'single' })} />
            Different left &amp; right amounts
          </label>

          {m.mode === 'split' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={{ display: 'block' }}>
                <div style={{ ...label, marginBottom: 3 }}>Left</div>
                <input style={small} value={m.left} onChange={e => update(i, { left: e.target.value })} placeholder="e.g. 45s" />
              </label>
              <label style={{ display: 'block' }}>
                <div style={{ ...label, marginBottom: 3 }}>Right</div>
                <input style={small} value={m.right} onChange={e => update(i, { right: e.target.value })} placeholder="e.g. 30s" />
              </label>
            </div>
          ) : (
            <label style={{ display: 'block' }}>
              <div style={{ ...label, marginBottom: 3 }}>Amount</div>
              <input style={small} value={m.amount} onChange={e => update(i, { amount: e.target.value })} placeholder="e.g. 45s, 10 reps, 3 breaths" />
            </label>
          )}
        </div>
      ))}

      <button onClick={add} style={{ color: '#1565C0', fontWeight: 700, fontSize: '0.84rem', marginTop: 2 }}>+ Add a stretch</button>

      {list.length > 0 && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid #D7EBFB', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--grey-700)' }}>
          <input type="checkbox" checked={!!inProgramme}
            onChange={e => onInProgrammeChange(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>Add to the programme</strong> — shows before every workout in this phase.
            <span style={{ color: 'var(--grey-500)' }}> Leave unticked to perform it as its own session.</span>
          </span>
        </label>
      )}
    </div>
  );
}

// Remember whether this device prefers guided ("play") or classic logging.
const LOG_MODE_KEY = 'tb_log_mode';
function readLogMode() {
  try { return window.localStorage.getItem(LOG_MODE_KEY) === 'classic' ? 'classic' : 'play'; }
  catch (_) { return 'play'; }
}
function writeLogMode(m) {
  try { window.localStorage.setItem(LOG_MODE_KEY, m === 'classic' ? 'classic' : 'play'); } catch (_) {}
}

// Round a weight to a sensible 0.25kg grid so the +/- steppers stay tidy.
function roundWeight(n) { return Math.round(n * 4) / 4; }

// Build the ordered list of "steps" (one logged set each) for guided mode.
// Supersets (A1/A2…) are interleaved — A1 set 1, A2 set 1, A1 set 2, … — so
// the panel walks the workout exactly as it's written. The last sub-exercise
// of a group in a given round carries the rest, so you don't rest mid-superset.
function buildPlaySteps(rows) {
  const groups = [];
  let cur = null;
  (rows || []).forEach((r, exIdx) => {
    const m = String(r.exercise_code || '').match(/^[A-Za-z]+/);
    const key = m ? m[0].toUpperCase() : `solo-${exIdx}`;
    if (!cur || cur.key !== key) { cur = { key, items: [] }; groups.push(cur); }
    cur.items.push(exIdx);
  });
  const steps = [];
  groups.forEach(g => {
    const maxSets = Math.max(1, ...g.items.map(exIdx => (rows[exIdx].sets || []).length));
    for (let round = 0; round < maxSets; round++) {
      const present = g.items.filter(exIdx => round < (rows[exIdx].sets || []).length);
      present.forEach((exIdx, idx) => {
        steps.push({
          exIdx,
          setIdx: round,
          roundIdx: round,
          roundCount: (rows[exIdx].sets || []).length,
          supersetSize: g.items.length,
          restAfter: idx === present.length - 1,
        });
      });
    }
  });
  return steps;
}

// ── Guided "play" workout panel ───────────────────────────────────────
// Walks the member through one set at a time with +/- steppers, preloads the
// same set from last time (or the coach's target on a first run), auto-counts
// rest between sets, and writes straight into the shared `rows` state so the
// classic form and the on-device draft stay perfectly in sync.
function PlayWorkout({
  rows, updateSet, isCardio, lastFor, lastReady,
  mobRows, toggleMob, phaseMobility,
  programmeName, phaseName, notes, setNotes,
  onFinish, saving, error, onSwitchToClassic, onCancel,
  restored, onDiscard, catalogue, onSwap,
  isBodyweight, hasBodyweight, bodyWeight, setBodyWeight, scanWeight,
}) {
  const steps = useMemo(() => buildPlaySteps(rows), [rows]);
  const rowsRef = useRef(rows); rowsRef.current = rows;

  const startPos = mobRows.length ? -1 : 0;
  const [pos, setPos] = useState(startPos);
  const [rest, setRest] = useState(null); // seconds remaining, or null when not resting
  const [restTotal, setRestTotal] = useState(0); // the full rest length, for the bar
  const [swapOpen, setSwapOpen] = useState(false); // mid-workout exercise swap picker
  const [notesOpen, setNotesOpen] = useState(false); // inline notes box on the guided screen

  // Preload the current set once we know last time's numbers — same set index
  // from the previous attempt, else the coach's target, else leave blank.
  useEffect(() => {
    if (pos < 0 || pos >= steps.length || !lastReady) return;
    const st = steps[pos];
    const r = rowsRef.current[st.exIdx];
    if (!r) return;
    if (isCardio(r.exercise_name)) return; // cardio uses its own fields, no preload
    const s = r.sets[st.setIdx];
    if (!s || s.weight !== '' || s.reps !== '') return; // already has input
    const last = lastFor(r.exercise_name);
    const li = last && Array.isArray(last.sets) ? last.sets[st.setIdx] : null;
    let w = null, reps = null;
    if (li) { w = li.weight; reps = li.reps; }
    else { w = (r.target_weight == null || r.target_weight === '') ? null : r.target_weight; reps = r.target_reps; }
    // Bodyweight moves: the weight box is a signed +belt/−assist extra, so we
    // never preload last time's effective load into it — start it blank (= BW).
    const bw = isBodyweight && isBodyweight(r.exercise_name);
    if (!bw && w != null && w !== '') updateSet(st.exIdx, st.setIdx, 'weight', String(w), false);
    if (reps != null && reps !== '') updateSet(st.exIdx, st.setIdx, 'reps', String(reps), false);
  }, [pos, lastReady]);

  // Rest countdown. When it reaches zero we DON'T auto-advance — the timer is
  // just a guide. Equipment can be busy, so we hold on a "rest's up" screen and
  // wait for the member to tap "Start next set". We buzz once on hitting zero.
  useEffect(() => {
    if (rest == null) return undefined;
    if (rest <= 0) return undefined; // reached zero: stay put until the user taps
    const t = setTimeout(() => setRest(r => {
      if (r == null) return null;
      const nx = r - 1;
      if (nx <= 0) {
        try { if (navigator.vibrate) navigator.vibrate(220); } catch (_) {}
        return 0;
      }
      return nx;
    }), 1000);
    return () => clearTimeout(t);
  }, [rest]);

  function logCurrent() {
    const st = steps[pos];
    const r = rowsRef.current[st.exIdx];
    const secs = Number(r && r.rest_seconds);
    const isLast = pos >= steps.length - 1;
    // Advance to the next exercise straight away so the member can see it and
    // start loading the weights. The rest timer then rides along as a bar on
    // that screen, counting down without ever forcing the next move.
    setPos(p => p + 1);
    if (!isLast && st.restAfter && secs > 0) { setRestTotal(secs); setRest(secs); }
    else setRest(null);
  }
  function dismissRest() { setRest(null); }
  // Skip the rest of the current exercise — e.g. it hurts, or the member
  // just wants to move on. Jumps past every remaining set of this exercise
  // (supersets keep their other moves, which have a different exIdx).
  function skipExercise() {
    const curEx = steps[pos] ? steps[pos].exIdx : null;
    let p = pos + 1;
    while (p < steps.length && steps[p].exIdx === curEx) p += 1;
    setRest(null);
    setPos(p);
  }
  function goBack() {
    setRest(null);
    setPos(p => Math.max(startPos, p - 1));
  }
  function handleDiscard() {
    if (typeof window !== 'undefined' && window.confirm &&
        !window.confirm('Discard this session? Your entered sets will be cleared and it will show as "Start workout" again.')) return;
    if (onDiscard) onDiscard();
    setRest(null);
    setPos(startPos);
  }

  const headerBar = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={label}>Guided workout</div>
        <div style={{ fontWeight: 800, fontSize: '1.1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{programmeName}</div>
        <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>{phaseName}</div>
      </div>
      <button className="btn btn-secondary" style={{ padding: '8px 14px', flexShrink: 0 }} onClick={onCancel}>Close</button>
    </div>
  );
  const switchLink = (
    <div style={{ textAlign: 'center', marginTop: 14 }}>
      <button onClick={onSwitchToClassic} style={{ color: 'var(--grey-500)', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'underline' }}>
        Prefer typing? Use the classic form (add notes for your coach)
      </button>
    </div>
  );

  // ── Warm-up screen ──────────────────────────────────────────────────
  if (pos === -1) {
    return (
      <div style={card}>
        {headerBar}
        {phaseMobility && phaseMobility.length > 0 && <MobilityView items={phaseMobility} />}
        <div style={{ border: '1px solid #FFD2B8', background: '#FFF3EC', borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', background: '#FF6200', borderRadius: 999, padding: '2px 10px', letterSpacing: '0.06em' }}>WARM-UP</span>
            <span style={{ fontWeight: 800 }}>Mobility first</span>
          </div>
          {mobRows.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i ? '1px solid #FFD2B8' : 'none' }}>
              <button type="button" onClick={() => toggleMob(i)} aria-label="Mark done"
                style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, border: `2px solid ${m.done ? '#FF6200' : '#FFC2A0'}`, background: m.done ? '#FF6200' : '#fff', color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1 }}>
                {m.done ? '✓' : ''}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.exercise_name}</div>
                {m.target ? <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>target {m.target}</div> : null}
              </div>
            </div>
          ))}
        </div>
        <button className="btn btn-primary btn-full" onClick={() => setPos(0)}>
          {steps.length ? 'Start workout →' : 'Finish & save'}
        </button>
        {restored && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button onClick={handleDiscard} style={{ color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 600 }}>
              Discard this session & start fresh
            </button>
          </div>
        )}
        {switchLink}
      </div>
    );
  }

  // ── Empty workout ───────────────────────────────────────────────────
  if (steps.length === 0) {
    return (
      <div style={card}>
        {headerBar}
        <div style={{ color: 'var(--grey-500)', fontSize: '0.9rem', padding: '8px 0 16px' }}>This workout has no exercises yet.</div>
        {switchLink}
      </div>
    );
  }

  // ── Finish screen ───────────────────────────────────────────────────
  if (pos >= steps.length) {
    let done = 0;
    rows.forEach(r => (r.sets || []).forEach(s => { if (s.weight !== '' || s.reps !== '' || s.duration_min !== '' || s.distance !== '' || s.calories !== '' || s.resistance !== '') done += 1; }));
    return (
      <div style={card}>
        {headerBar}
        <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
          <div style={{ fontSize: '2.4rem' }}>🎉</div>
          <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Workout complete</div>
          <div style={{ color: 'var(--grey-500)', fontSize: '0.9rem', marginTop: 2 }}>{done} set{done === 1 ? '' : 's'} logged</div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={label}>Note for your coach (optional)</div>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={notes}
            onChange={e => setNotes(e.target.value)} placeholder="Anything about the whole session?" />
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 4 }}>
            Want to leave feedback on individual sets? Switch to the classic form before saving.
          </div>
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 10 }}>{error}</div>}
        <button className="btn btn-primary btn-full" style={{ marginTop: 12 }} onClick={onFinish} disabled={saving}>
          {saving ? 'Saving…' : 'Finish & save session'}
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12 }}>
          <button onClick={() => setPos(steps.length - 1)} style={{ color: 'var(--grey-500)', fontSize: '0.8rem', fontWeight: 600 }}>← Back to last set</button>
          <button onClick={onSwitchToClassic} style={{ color: 'var(--grey-500)', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'underline' }}>Classic form</button>
        </div>
      </div>
    );
  }

  // ── A single set ────────────────────────────────────────────────────
  const st = steps[pos];
  const r = rows[st.exIdx];
  const s = r.sets[st.setIdx] || blankSet();
  const cardio = isCardio(r.exercise_name);
  const bw = !cardio && isBodyweight && isBodyweight(r.exercise_name);
  const bwNum = bodyWeight === '' ? null : Number(bodyWeight);
  const delta = s.weight === '' ? 0 : Number(s.weight);
  const effective = (bw && bwNum != null && !Number.isNaN(bwNum)) ? roundWeight(Math.max(0, bwNum + delta)) : null;
  const last = lastFor(r.exercise_name);
  const lastSet = last && Array.isArray(last.sets) ? last.sets[st.setIdx] : null;
  const progressPct = Math.round((pos / steps.length) * 100);

  const bigNum = { flex: 1, minWidth: 0, width: '100%', textAlign: 'center', fontSize: '1.8rem', fontWeight: 800, padding: '12px 4px', borderRadius: 12, border: '1px solid var(--grey-300)', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box' };
  const stepBtn = { width: 56, height: 56, borderRadius: 12, border: '1px solid var(--grey-300)', background: 'var(--grey-50)', fontSize: '1.6rem', fontWeight: 800, color: 'var(--grey-700)', lineHeight: 1, flexShrink: 0 };
  const fineBtn = { padding: '5px 14px', borderRadius: 999, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.8rem', fontWeight: 700, color: 'var(--grey-500)', lineHeight: 1 };

  function adjust(field, d) {
    const raw = s[field];
    let next;
    // Bodyweight moves allow a negative weight (machine/band assistance).
    if (field === 'weight') next = roundWeight(Math.max(bw ? -500 : 0, (raw === '' ? 0 : Number(raw)) + d));
    else next = Math.max(0, (raw === '' ? 0 : Number(raw)) + d);
    updateSet(st.exIdx, st.setIdx, field, String(next));
  }

  // Inline rest bar — rides along on the weight/reps screen so the next exercise
  // is already visible and the member can load the weights while it counts down.
  // Never forces a move: it just empties to zero and turns orange ("rest's up").
  let restBar = null;
  if (rest != null) {
    const mm = Math.floor(rest / 60), ss = rest % 60;
    const ready = rest <= 0;
    const barPct = ready ? 100 : (restTotal > 0 ? Math.min(100, Math.round((rest / restTotal) * 100)) : 0);
    restBar = (
      <div style={{ border: `1px solid ${ready ? '#FFC2A0' : 'var(--grey-300)'}`, background: ready ? '#FFF3EC' : 'var(--grey-50)', borderRadius: 12, padding: '10px 12px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <span style={{ ...label, color: ready ? '#FF6200' : 'var(--green)', marginBottom: 0 }}>{ready ? "Rest's up" : 'Rest'}</span>
            <span style={{ fontSize: '1.3rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: ready ? '#FF6200' : undefined, lineHeight: 1 }}>{mm}:{String(ss).padStart(2, '0')}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button style={fineBtn} onClick={() => setRest(rr => Math.max(0, (rr || 0) - 15))} aria-label="Rest 15s less">−15</button>
            <button style={fineBtn} onClick={() => setRest(rr => { const nx = (rr || 0) + 15; setRestTotal(t => Math.max(t, nx)); return nx; })} aria-label="Rest 15s more">+15</button>
            <button style={{ ...fineBtn, color: ready ? '#FF6200' : 'var(--grey-700)', borderColor: ready ? '#FFC2A0' : 'var(--grey-300)' }} onClick={dismissRest} aria-label="Dismiss rest timer">Dismiss</button>
          </div>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: 'var(--grey-100)', overflow: 'hidden', marginTop: 8 }}>
          <div style={{ height: '100%', width: `${barPct}%`, background: ready ? '#FF6200' : 'var(--green)', borderRadius: 999, transition: 'width 1s linear' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      {headerBar}
      {restBar}

      {/* progress */}
      <div style={{ height: 6, borderRadius: 999, background: 'var(--grey-100)', marginBottom: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--green)', borderRadius: 999, transition: 'width 0.25s' }} />
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--grey-500)', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 12 }}>
        SET {pos + 1} OF {steps.length}
      </div>

      {/* exercise */}
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        {st.supersetSize > 1 && (
          <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', background: 'var(--green)', borderRadius: 999, padding: '2px 10px', letterSpacing: '0.06em' }}>
            {setTypeLabel(st.supersetSize)} {st.groupKey || ''}
          </span>
        )}
        <div style={{ fontWeight: 800, fontSize: '1.3rem', marginTop: 6 }}>
          {r.exercise_code ? <span style={{ color: 'var(--grey-500)', marginRight: 8 }}>{r.exercise_code}</span> : null}
          {r.exercise_name}
        </div>
        <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginTop: 2 }}>
          Set {st.setIdx + 1} of {st.roundCount}
          {!cardio && r.target_reps ? ` · target ${r.target_reps} reps` : ''}
          {!cardio && r.tempo ? ` · tempo ${r.tempo}` : ''}
        </div>
        {!cardio && lastSet && (
          <div style={{ display: 'inline-block', marginTop: 8, fontSize: '0.75rem', color: 'var(--grey-700)', background: 'var(--green-pale)', borderRadius: 6, padding: '4px 10px' }}>
            <span style={{ fontWeight: 700, color: 'var(--green)' }}>Last time:</span> {lastSet.weight != null ? `${lastSet.weight}kg` : 'BW'} × {lastSet.reps != null ? lastSet.reps : '–'}
          </div>
        )}
      </div>

      {/* Bodyweight banner — the load for dips/chins etc. comes from the
          member's bodyweight, so capture it (prefilled from their last scan). */}
      {bw && (
        <div style={{ background: bwNum == null ? '#FFF8F0' : 'var(--green-pale)', border: `1px solid ${bwNum == null ? '#FFD2B8' : 'var(--green-pale)'}`, borderRadius: 10, padding: '10px 12px', margin: '6px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ ...label, marginBottom: 0, color: bwNum == null ? '#C24E00' : 'var(--green)' }}>Your bodyweight</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" inputMode="decimal" value={bodyWeight}
                onChange={e => setBodyWeight(e.target.value)}
                placeholder="kg"
                style={{ width: 84, textAlign: 'center', fontWeight: 800, fontSize: '1rem', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--grey-300)' }} />
              <span style={{ fontWeight: 700, color: 'var(--grey-500)' }}>kg</span>
            </div>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 4 }}>
            {scanWeight != null ? 'From your last InBody scan — tweak if it’s changed.' : 'No scan on file yet — pop your current bodyweight in so these sets count.'}
          </div>
        </div>
      )}

      {/* inputs */}
      {cardio ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '14px 0' }}>
          <label><div style={{ ...label, marginBottom: 3 }}>Time (min)</div>
            <input type="number" inputMode="decimal" style={inputStyle} value={s.duration_min} onChange={e => updateSet(st.exIdx, st.setIdx, 'duration_min', e.target.value)} placeholder="–" /></label>
          <label><div style={{ ...label, marginBottom: 3 }}>Distance</div>
            <input type="number" inputMode="decimal" style={inputStyle} value={s.distance} onChange={e => updateSet(st.exIdx, st.setIdx, 'distance', e.target.value)} placeholder="m / km" /></label>
          <label><div style={{ ...label, marginBottom: 3 }}>Resistance</div>
            <input type="number" inputMode="decimal" style={inputStyle} value={s.resistance} onChange={e => updateSet(st.exIdx, st.setIdx, 'resistance', e.target.value)} placeholder="–" /></label>
          <label><div style={{ ...label, marginBottom: 3 }}>Calories</div>
            <input type="number" inputMode="numeric" style={inputStyle} value={s.calories} onChange={e => updateSet(st.exIdx, st.setIdx, 'calories', e.target.value)} placeholder="–" /></label>
        </div>
      ) : (
        <div style={{ margin: '16px 0' }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ ...label, textAlign: 'center', marginBottom: 6 }}>
              {bw ? '+ Belt / − Assist (kg)' : 'Weight (kg)'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={stepBtn} onClick={() => adjust('weight', -2.5)} aria-label="Less weight by 2.5kg">−</button>
              <input type="number" inputMode="decimal" style={bigNum} value={s.weight}
                onChange={e => updateSet(st.exIdx, st.setIdx, 'weight', e.target.value)} placeholder={bw ? '0' : 'BW'} />
              <button style={stepBtn} onClick={() => adjust('weight', 2.5)} aria-label="More weight by 2.5kg">+</button>
            </div>
            {/* Fine adjust for edge sets / microplates — big buttons do 2.5kg,
                these do 0.5kg, and the number itself is typeable for 0.25s. */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
              <button style={fineBtn} onClick={() => adjust('weight', -0.5)} aria-label="Less weight by 0.5kg">−0.5</button>
              <button style={fineBtn} onClick={() => adjust('weight', 0.5)} aria-label="More weight by 0.5kg">+0.5</button>
            </div>
            {bw && (
              <div style={{ textAlign: 'center', fontSize: '0.74rem', color: 'var(--grey-500)', marginTop: 8 }}>
                {delta === 0 ? 'Bodyweight only' : delta > 0 ? `Bodyweight + ${roundWeight(delta)}kg` : `Assisted ${roundWeight(-delta)}kg`}
                {effective != null && <> · counts as <strong style={{ color: 'var(--green)' }}>{effective}kg</strong></>}
              </div>
            )}
          </div>
          <div>
            <div style={{ ...label, textAlign: 'center', marginBottom: 6 }}>Reps</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={stepBtn} onClick={() => adjust('reps', -1)} aria-label="Fewer reps">−</button>
              <input type="number" inputMode="numeric" style={bigNum} value={s.reps}
                onChange={e => updateSet(st.exIdx, st.setIdx, 'reps', e.target.value)} placeholder="–" />
              <button style={stepBtn} onClick={() => adjust('reps', 1)} aria-label="More reps">+</button>
            </div>
          </div>
        </div>
      )}

      <button className="btn btn-primary btn-full" onClick={logCurrent}>
        {pos === steps.length - 1 ? 'Log set & finish →' : (st.restAfter && Number(r.rest_seconds) > 0 ? 'Log set & rest →' : 'Log set →')}
      </button>
      {/* Mid-workout changes — swap if a machine's taken, skip if it hurts. */}
      {!cardio && onSwap && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 12 }}>
          <button onClick={() => setSwapOpen(true)}
            style={{ ...fineBtn, color: 'var(--grey-700)' }}>⇄ Swap exercise</button>
          <button onClick={skipExercise}
            style={{ ...fineBtn, color: 'var(--grey-700)' }}>Skip exercise →</button>
        </div>
      )}

      {/* Quick note on THIS set — jot how it felt, a niggle, etc. Each set
          keeps its own note, saved with that set. */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
        <button onClick={() => setNotesOpen(o => !o)}
          style={{ ...fineBtn, color: 'var(--grey-700)' }}>
          {notesOpen ? '− Hide note' : ((r.sets[st.setIdx] && r.sets[st.setIdx].note && r.sets[st.setIdx].note.trim()) ? '📝 Edit note on this set' : '📝 Add a note to this set')}
        </button>
      </div>
      {notesOpen && (
        <textarea value={(r.sets[st.setIdx] && r.sets[st.setIdx].note) || ''}
          onChange={e => updateSet(st.exIdx, st.setIdx, 'note', e.target.value)} autoFocus
          placeholder={`Note for set ${st.setIdx + 1} — how did it feel?`}
          style={{ width: '100%', minHeight: 64, marginTop: 8, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--grey-300)', fontSize: '0.92rem', boxSizing: 'border-box' }} />
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 12 }}>
        <button onClick={goBack} disabled={pos <= startPos}
          style={{ color: pos <= startPos ? 'var(--grey-300)' : 'var(--grey-500)', fontSize: '0.82rem', fontWeight: 600 }}>← Back</button>
        <button onClick={handleDiscard}
          style={{ color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 600 }}>Discard session</button>
      </div>
      {switchLink}

      {swapOpen && (
        <ExercisePickerModal
          catalogue={catalogue || []}
          value={r.exercise_name}
          onClose={() => setSwapOpen(false)}
          onPick={(name, part) => {
            setSwapOpen(false);
            if (!name) return;
            // Custom swap mid-workout: just capture a name for this session.
            if (name === '__custom__') {
              const typed = (typeof window !== 'undefined' && window.prompt)
                ? window.prompt(part ? `Swap to (${part}):` : 'Swap to exercise:') : '';
              const nm = (typed || '').trim();
              if (nm && onSwap) onSwap(st.exIdx, nm);
              return;
            }
            if (onSwap) onSwap(st.exIdx, name);
          }}
        />
      )}
    </div>
  );
}

function LogSession({ phase, programme, onDone, onCancel }) {
  // A standalone mobility programme logs holds, not weight/reps.
  const isMobilityProg = programme.kind === 'mobility';
  // Pre-workout mobility blocks on this phase ride along with EVERY workout.
  const preMobProgs = (phase.programmes || []).filter(
    p => p.kind === 'mobility' && p.placement === 'preworkout');

  const buildInitial = () =>
    isMobilityProg ? [] : (programme.exercises || []).map(ex => {
      const n = Math.max(1, ex.sets || 1);
      return {
        exercise_id: ex.id,
        exercise_name: ex.name,
        exercise_code: ex.code,
        target_reps: ex.reps,
        target_weight: ex.target_weight == null ? null : ex.target_weight,
        // Cardio targets set by the coach, shown as the goal to hit.
        t_time: ex.t_time == null ? null : ex.t_time,
        t_distance: ex.t_distance == null ? null : ex.t_distance,
        t_resistance: ex.t_resistance == null ? null : ex.t_resistance,
        t_calories: ex.t_calories == null ? null : ex.t_calories,
        // Original template meta — kept so "save to my programme" can rebuild
        // the phase faithfully for exercises that weren't touched.
        tempo: ex.tempo || null,
        rest_seconds: ex.rest_seconds == null ? null : ex.rest_seconds,
        tmpl_notes: ex.notes || null,
        sets: Array.from({ length: n }, () => blankSet()),
      };
    });

  const [rows, setRows]   = useState(() => {
    const d = readDraft(programme.id);
    return d && Array.isArray(d.rows) && d.rows.length ? d.rows : buildInitial();
  });
  const [notes, setNotes] = useState(() => {
    const d = readDraft(programme.id);
    return d && typeof d.notes === 'string' ? d.notes : '';
  });
  const [restored, setRestored] = useState(() => {
    const d = readDraft(programme.id);
    return !!(d && Array.isArray(d.rows) && d.rows.length);
  });
  // True once the person logging actually edits something. Pre-filled coach
  // targets don't set this, so merely opening a workout never makes a draft.
  // Seeded true when we resumed a real saved draft.
  const touchedRef = useRef(false);
  useEffect(() => { if (restored) touchedRef.current = true; }, [restored]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lastLogs, setLastLogs] = useState({}); // keyed by lower(exercise_name)
  const [lastReady, setLastReady] = useState(false);
  // Guided ("play") mode is the default for workouts; mobility always uses the
  // classic tick list. The choice is remembered per device.
  const [mode, setMode] = useState(() => (isMobilityProg ? 'classic' : readLogMode()));
  function switchMode(m) { setMode(m); if (!isMobilityProg) writeLogMode(m); }

  // Mobility moves for this session: either the standalone mobility
  // programme's own moves, or (on a normal workout) every pre-workout
  // mobility block flattened into one tickable warm-up list. Kept out of the
  // device draft on purpose — it's quick to re-tick and avoids edge cases.
  const buildMob = () => {
    const src = isMobilityProg
      ? (programme.exercises || [])
      : preMobProgs.flatMap(p => p.exercises || []);
    return src.map(e => ({
      exercise_id: e.id || null,
      exercise_name: e.name,
      target: e.reps || '',
      done: false,
      hold: '',
    }));
  };
  const [mobRows, setMobRows] = useState(buildMob);
  function toggleMob(i) {
    setMobRows(prev => prev.map((m, j) => j === i ? { ...m, done: !m.done } : m));
  }

  // Exercise library → map name→body_part so we know which exercises are
  // cardio (time/distance/resistance/calories) vs strength (weight/reps).
  const [catalogue, setCatalogue] = useState([]);
  const catMap = useMemo(() => {
    const m = {};
    for (const e of catalogue) if (e.name) m[e.name.trim().toLowerCase()] = e.body_part;
    return m;
  }, [catalogue]);
  // name → load_type ('weighted' | 'bodyweight'). Bodyweight moves (dips,
  // chin-ups…) are logged as reps ± belt/assist, with the member's bodyweight
  // supplying the load so the set still counts toward volume.
  const catLoadMap = useMemo(() => {
    const m = {};
    for (const e of catalogue) if (e.name) m[e.name.trim().toLowerCase()] = e.load_type || 'weighted';
    return m;
  }, [catalogue]);
  useEffect(() => {
    supabase.rpc('get_exercise_catalogue').then(({ data }) => setCatalogue(data || [])).catch(() => {});
  }, []);
  function isCardio(name) {
    return (catMap[(name || '').trim().toLowerCase()] || '') === 'Cardio';
  }
  function isBodyweight(name) {
    return (catLoadMap[(name || '').trim().toLowerCase()] || '') === 'bodyweight';
  }

  // Session bodyweight for bodyweight exercises — prefilled from the member's
  // most recent InBody scan, editable, and prompted for if there's no scan.
  const [scanWeight, setScanWeight] = useState(null);   // kg from latest scan, or null
  const [bodyWeight, setBodyWeight] = useState('');      // the value used this session
  useEffect(() => {
    supabase.rpc('get_my_inbody_scans').then(({ data }) => {
      const scans = Array.isArray(data) ? data : [];
      const w = scans.length && scans[0].weight_kg != null ? Number(scans[0].weight_kg) : null;
      if (w != null && !Number.isNaN(w)) { setScanWeight(w); setBodyWeight(String(w)); }
    }).catch(() => {});
  }, []);
  // Does this workout include any bodyweight movement?
  const hasBodyweight = useMemo(
    () => rows.some(r => (catLoadMap[(r.exercise_name || '').trim().toLowerCase()] || '') === 'bodyweight'),
    [rows, catLoadMap]
  );

  // Mid-workout editing — add/remove exercises for today only, with an
  // option to also save those changes back to the programme template.
  const [editMode, setEditMode] = useState(false);
  const [saveToTemplate, setSaveToTemplate] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  // Auto-save the in-progress session to this device whenever anything
  // changes — but only once there's real input, so an untouched form never
  // creates a lingering "continue" state.
  const setHasInput = (s) =>
    s.weight !== '' || s.reps !== '' || (s.note && s.note !== '') ||
    s.duration_min !== '' || s.distance !== '' || s.resistance !== '' || s.calories !== '';
  useEffect(() => {
    const hasInput =
      touchedRef.current &&
      (rows.some(r => r.sets.some(setHasInput)) || (notes && notes.trim() !== ''));
    if (hasInput) writeDraft(programme.id, { rows, notes, savedAt: Date.now() });
    else clearDraft(programme.id);
  }, [rows, notes, programme.id]);

  function startFresh() {
    clearDraft(programme.id);
    touchedRef.current = false;
    setRows(buildInitial());
    setNotes('');
    setRestored(false);
  }

  // Pull the member's most recent logged sets for each exercise so we can
  // show a "last time" line — gives them a number to beat.
  useEffect(() => {
    supabase.rpc('get_my_last_exercise_logs')
      .then(({ data }) => { if (data) setLastLogs(data); })
      .then(() => setLastReady(true))
      .catch(() => setLastReady(true));
  }, []);

  function lastFor(name) {
    if (!name) return null;
    return lastLogs[name.trim().toLowerCase()] || null;
  }
  function lastSummary(last) {
    if (!last || !Array.isArray(last.sets) || last.sets.length === 0) return null;
    const sets = last.sets.map(s => `${s.weight != null ? s.weight + 'kg' : 'BW'}×${s.reps != null ? s.reps : '–'}`);
    let when = '';
    try { when = format(parseISO(last.performed_on), 'd MMM'); } catch (_) {}
    return { when, text: sets.join(', ') };
  }

  function updateSet(exIdx, setIdx, field, value, fromUser = true) {
    // Only a genuine edit by the person logging counts as "in progress".
    // The guided screen pre-fills the coach's targets/last numbers (fromUser
    // false) — those must NOT create a saved draft, or every workout the
    // member merely opens would wrongly show "Continue".
    if (fromUser) touchedRef.current = true;
    setRows(prev => prev.map((r, i) => {
      if (i !== exIdx) return r;
      return { ...r, sets: r.sets.map((s, j) => (j === setIdx ? { ...s, [field]: value } : s)) };
    }));
  }
  function noteEdit(v) { touchedRef.current = true; setNotes(v); }
  function addSet(exIdx) {
    setRows(prev => prev.map((r, i) => i === exIdx ? { ...r, sets: [...r.sets, blankSet()] } : r));
  }
  function removeSet(exIdx, setIdx) {
    setRows(prev => prev.map((r, i) => i === exIdx ? { ...r, sets: r.sets.filter((_, j) => j !== setIdx) } : r));
  }
  function removeExercise(exIdx) {
    setRows(prev => prev.filter((_, i) => i !== exIdx));
  }
  function addExercise(name) {
    setRows(prev => [...prev, {
      exercise_id: null, exercise_name: name, exercise_code: '',
      target_reps: '', tempo: null, rest_seconds: null, tmpl_notes: null,
      sets: [blankSet()],
    }]);
    setPickerOpen(false);
    setPickerSearch('');
  }
  // Mid-workout swap (guided mode): machine taken, or they want a different
  // movement. Keep the slot (code, sets structure) but change the exercise and
  // drop any coach target weight / previous id so stale numbers don't carry.
  function swapExercise(exIdx, name) {
    if (!name) return;
    touchedRef.current = true;
    setRows(prev => prev.map((r, i) => {
      if (i !== exIdx) return r;
      return {
        ...r,
        exercise_id: null,
        exercise_name: name,
        target_weight: null,
        sets: r.sets.map(() => blankSet()),
      };
    }));
  }

  // Grouped/ordered catalogue for the mid-workout picker, filtered by search.
  const pickerGroups = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const g = {};
    for (const e of catalogue) {
      if (!e.name) continue;
      if (q && !e.name.toLowerCase().includes(q)) continue;
      (g[e.body_part] = g[e.body_part] || []).push(e.name);
    }
    const parts = Object.keys(g).sort((a, b) => {
      const ia = BODY_PART_ORDER.indexOf(a), ib = BODY_PART_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1; if (ib === -1) return -1; return ia - ib;
    });
    return parts.map(p => [p, g[p].sort((a, b) => a.localeCompare(b))]);
  }, [catalogue, pickerSearch]);

  async function save() {
    setSaving(true); setError('');
    const sets = [];
    let needBodyWeight = false; // a bodyweight set was logged but we've no bodyweight to load it with
    const bw = bodyWeight === '' ? null : Number(bodyWeight);
    rows.forEach(r => {
      const cardio = isCardio(r.exercise_name);
      const bodyweight = !cardio && isBodyweight(r.exercise_name);
      r.sets.forEach((s, j) => {
        if (!setHasInput(s)) return;
        // For a bodyweight move the "weight" box holds a signed extra load
        // (+ belt / − assist); the real load is bodyweight + that. We store
        // the effective weight so volume maths is unchanged, plus the
        // breakdown for the record.
        let weight = cardio || s.weight === '' ? null : Number(s.weight);
        let body_weight = null, load_delta = null;
        if (bodyweight) {
          const delta = s.weight === '' ? 0 : Number(s.weight);
          load_delta = Number.isNaN(delta) ? 0 : delta;
          if (bw == null || Number.isNaN(bw)) { needBodyWeight = true; weight = null; }
          else { body_weight = bw; weight = roundWeight(Math.max(0, bw + load_delta)); }
        }
        sets.push({
          exercise_id: r.exercise_id,
          exercise_name: r.exercise_name,
          exercise_code: r.exercise_code,
          set_number: j + 1,
          // Strength (effective, for bodyweight moves)
          weight,
          reps:   cardio || s.reps === ''   ? null : Number(s.reps),
          // Bodyweight breakdown (null for ordinary weighted / cardio sets)
          body_weight,
          load_delta,
          // Cardio
          duration_min: !cardio || s.duration_min === '' ? null : Number(s.duration_min),
          distance:     !cardio || s.distance === ''     ? null : Number(s.distance),
          resistance:   !cardio || s.resistance === ''   ? null : Number(s.resistance),
          calories:     !cardio || s.calories === ''     ? null : Number(s.calories),
          // Per-set note (both)
          note: s.note && s.note.trim() ? s.note.trim() : null,
        });
      });
    });

    if (needBodyWeight) {
      setError('Add your current bodyweight at the top so your bodyweight sets (dips, chins…) count toward your volume.');
      setSaving(false);
      return;
    }

    // Mobility holds → weight/reps null so they stay out of strength/volume
    // charts. A ticked-but-untimed move stores hold_seconds 0 ("done").
    mobRows.forEach((m, i) => {
      if (!m.done && m.hold === '') return;
      sets.push({
        exercise_id: m.exercise_id,
        exercise_name: m.exercise_name,
        exercise_code: null,
        set_number: i + 1,
        weight: null,
        reps: null,
        duration_min: null,
        distance: null,
        resistance: null,
        calories: null,
        hold_seconds: m.hold === '' ? 0 : Number(m.hold),
        note: null,
      });
    });

    if (sets.length === 0) {
      setError(isMobilityProg ? 'Tick at least one stretch before saving.' : 'Log at least one set before saving.');
      setSaving(false); return;
    }

    const { error: rpcErr } = await supabase.rpc('log_training_session', {
      payload: {
        programme_id: programme.id,
        phase_id: phase.id,
        programme_name: programme.name,
        phase_name: phase.name,
        notes: notes || null,
        sets,
      },
    });
    if (rpcErr) { setSaving(false); setError(rpcErr.message || 'Could not save session.'); return; }

    // Optionally persist the edited exercise list back to the programme
    // template (only this workout within the phase is rewritten).
    if (saveToTemplate && phase && phase.id) {
      const progs = (phase.programmes || []).map(pr => {
        const exercises = pr.id === programme.id
          ? rows.map(r => ({
              code: (r.exercise_code || '').trim() || null,
              name: r.exercise_name,
              sets: r.sets.length,
              reps: (r.target_reps || '') === '' ? null : r.target_reps,
              tempo: r.tempo || null,
              rest_seconds: r.rest_seconds == null ? null : r.rest_seconds,
              notes: r.tmpl_notes || null,
              target_weight: r.target_weight == null || r.target_weight === '' ? null : Number(r.target_weight),
              t_time: r.t_time == null ? null : r.t_time,
              t_distance: r.t_distance == null ? null : r.t_distance,
              t_resistance: r.t_resistance == null ? null : r.t_resistance,
              t_calories: r.t_calories == null ? null : r.t_calories,
            }))
          : (pr.exercises || []).map(e => ({
              code: e.code || null, name: e.name,
              sets: e.sets == null ? null : e.sets, reps: e.reps || null,
              tempo: e.tempo || null, rest_seconds: e.rest_seconds == null ? null : e.rest_seconds,
              notes: e.notes || null,
              target_weight: e.target_weight == null ? null : e.target_weight,
              t_time: e.t_time == null ? null : e.t_time,
              t_distance: e.t_distance == null ? null : e.t_distance,
              t_resistance: e.t_resistance == null ? null : e.t_resistance,
              t_calories: e.t_calories == null ? null : e.t_calories,
            }));
        return { name: pr.name || 'Workout', exercises };
      });
      await supabase.rpc('update_my_phase', { p_phase_id: phase.id, payload: { name: phase.name, mobility: phase.mobility || [], programmes: progs } }).catch(() => {});
    }

    setSaving(false);
    clearDraft(programme.id);
    onDone();
  }

  // Guided mode (default for workouts) — one set at a time with steppers.
  if (mode === 'play' && !isMobilityProg) {
    return (
      <PlayWorkout
        rows={rows}
        updateSet={updateSet}
        isCardio={isCardio}
        lastFor={lastFor}
        lastReady={lastReady}
        mobRows={mobRows}
        toggleMob={toggleMob}
        phaseMobility={phase.mobility}
        programmeName={programme.name}
        phaseName={phase.name}
        notes={notes}
        setNotes={noteEdit}
        onFinish={save}
        saving={saving}
        error={error}
        onSwitchToClassic={() => switchMode('classic')}
        onCancel={onCancel}
        restored={restored}
        onDiscard={startFresh}
        catalogue={catalogue}
        onSwap={swapExercise}
        isBodyweight={isBodyweight}
        hasBodyweight={hasBodyweight}
        bodyWeight={bodyWeight}
        setBodyWeight={setBodyWeight}
        scanWeight={scanWeight}
      />
    );
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={label}>Logging</div>
          <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>{programme.name}</div>
          <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>{phase.name}</div>
        </div>
        <button className="btn btn-secondary" style={{ padding: '8px 14px' }} onClick={onCancel}>Close</button>
      </div>

      {!isMobilityProg && (
        <div style={{ textAlign: 'right', marginBottom: 10 }}>
          <button onClick={() => switchMode('play')} style={{ color: 'var(--green)', fontSize: '0.78rem', fontWeight: 700 }}>
            ▶ Try guided mode
          </button>
        </div>
      )}

      {restored && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'var(--green-pale)', borderRadius: 8, padding: '8px 10px', marginBottom: 12, fontSize: '0.8rem', color: 'var(--grey-700)' }}>
          <span><span style={{ fontWeight: 700, color: 'var(--green)' }}>Picked up where you left off.</span> Your entries are saved on this device.</span>
          <button onClick={startFresh} style={{ color: 'var(--grey-500)', fontWeight: 600, whiteSpace: 'nowrap', textDecoration: 'underline' }}>Start fresh</button>
        </div>
      )}

      {/* Bodyweight for bodyweight moves (dips/chins…) — prefilled from the
          member's last scan, editable, so those sets count toward volume. */}
      {hasBodyweight && (
        <div style={{ background: (bodyWeight === '' ? '#FFF8F0' : 'var(--green-pale)'), border: `1px solid ${bodyWeight === '' ? '#FFD2B8' : 'var(--green-pale)'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ ...label, marginBottom: 0, color: bodyWeight === '' ? '#C24E00' : 'var(--green)' }}>Your bodyweight</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" inputMode="decimal" value={bodyWeight}
                onChange={e => setBodyWeight(e.target.value)} placeholder="kg"
                style={{ width: 84, textAlign: 'center', fontWeight: 800, fontSize: '1rem', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--grey-300)' }} />
              <span style={{ fontWeight: 700, color: 'var(--grey-500)' }}>kg</span>
            </div>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 4 }}>
            {scanWeight != null ? 'From your last InBody scan — tweak if it’s changed. Used to load your dips, chins, etc.' : 'No scan on file yet — pop your current bodyweight in so these sets count.'}
          </div>
        </div>
      )}

      {/* Stretches & mobilisations — shown before every session, no logging */}
      {!isMobilityProg && <MobilityView items={phase.mobility} />}

      {/* Mobility moves — ticked off + optional hold time. Either the
          standalone mobility programme, or a pre-workout warm-up block. */}
      {mobRows.length > 0 && (
        <div style={{ border: '1px solid #FFD2B8', background: '#FFF3EC', borderRadius: 12, padding: 14, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', background: '#FF6200', borderRadius: 999, padding: '2px 10px', letterSpacing: '0.06em' }}>MOBILITY</span>
            <span style={{ fontWeight: 800 }}>{isMobilityProg ? programme.name : 'Pre-workout mobility'}</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--grey-700)', marginBottom: 10 }}>
            Tick each one off as you complete it.
          </div>
          {mobRows.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i ? '1px solid #FFD2B8' : 'none' }}>
              <button type="button" onClick={() => toggleMob(i)} aria-label="Mark done"
                style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, border: `2px solid ${m.done ? '#FF6200' : '#FFC2A0'}`, background: m.done ? '#FF6200' : '#fff', color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1, cursor: 'pointer' }}>
                {m.done ? '✓' : ''}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.exercise_name}</div>
                {m.target ? <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>target {m.target}</div> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mid-workout edit toggle */}
      {!isMobilityProg && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button type="button" onClick={() => setEditMode(m => !m)}
            style={{ fontWeight: 700, fontSize: '0.8rem', color: editMode ? 'var(--grey-500)' : 'var(--green)' }}>
            {editMode ? 'Done editing' : '✏️ Edit workout'}
          </button>
        </div>
      )}

      {!isMobilityProg && rows.length === 0 && (
        <div style={{ color: 'var(--grey-500)', fontSize: '0.9rem', padding: '8px 0' }}>
          This programme has no exercises yet{editMode ? ' — add one below.' : '.'}
        </div>
      )}

      {rows.map((r, exIdx) => {
        const cardio = isCardio(r.exercise_name);
        const bw = !cardio && isBodyweight(r.exercise_name);
        const bwNum = bodyWeight === '' ? null : Number(bodyWeight);
        return (
        <div key={r.exercise_id || `row-${exIdx}`} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontWeight: 700 }}>
              {r.exercise_code ? <span style={{ color: 'var(--grey-500)', marginRight: 6 }}>{r.exercise_code}</span> : null}
              {r.exercise_name}
              {cardio && <span style={{ marginLeft: 8, fontSize: '0.6rem', fontWeight: 800, color: partColor('Cardio'), background: '#FBE9E7', borderRadius: 999, padding: '2px 8px', letterSpacing: '0.06em' }}>CARDIO</span>}
            </div>
            {editMode
              ? <button onClick={() => removeExercise(exIdx)} style={{ color: 'var(--danger)', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Remove</button>
              : cardio
                ? (() => {
                    const t = [];
                    if (r.t_time != null) t.push(`${r.t_time} min`);
                    if (r.t_distance != null) t.push(`${r.t_distance} dist`);
                    if (r.t_resistance != null) t.push(`res ${r.t_resistance}`);
                    if (r.t_calories != null) t.push(`${r.t_calories} cal`);
                    return t.length ? <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>target {t.join(' · ')}</div> : null;
                  })()
                : (r.target_reps) ? <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>target {r.target_reps} reps</div> : null}
          </div>
          {!cardio && (() => {
            const sum = lastSummary(lastFor(r.exercise_name));
            return sum ? (
              <div style={{ marginTop: 4, fontSize: '0.72rem', color: 'var(--grey-700)', background: 'var(--green-pale)', borderRadius: 6, padding: '4px 8px' }}>
                <span style={{ fontWeight: 700, color: 'var(--green)' }}>Last time{sum.when ? ` (${sum.when})` : ''}:</span> {sum.text}
              </div>
            ) : null;
          })()}

          {cardio ? (
            // ── Cardio: time / distance / resistance / calories ──────────
            r.sets.map((s, setIdx) => (
              <div key={setIdx} style={{ background: 'var(--grey-50)', borderRadius: 8, padding: 10, marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: '0.6rem', color: 'var(--grey-500)', fontWeight: 700, letterSpacing: '0.08em' }}>EFFORT {setIdx + 1}</div>
                  <button onClick={() => removeSet(exIdx, setIdx)} disabled={r.sets.length <= 1}
                    style={{ color: r.sets.length <= 1 ? 'var(--grey-300)' : 'var(--danger)', fontSize: '1.1rem', lineHeight: 1 }}
                    aria-label="Remove effort">×</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label style={{ display: 'block' }}>
                    <div style={{ ...label, marginBottom: 3 }}>Time (min)</div>
                    <input type="number" inputMode="decimal" style={inputStyle} value={s.duration_min}
                      onChange={e => updateSet(exIdx, setIdx, 'duration_min', e.target.value)} placeholder="–" />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ ...label, marginBottom: 3 }}>Distance</div>
                    <input type="number" inputMode="decimal" style={inputStyle} value={s.distance}
                      onChange={e => updateSet(exIdx, setIdx, 'distance', e.target.value)} placeholder="m / km" />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ ...label, marginBottom: 3 }}>Resistance / level</div>
                    <input type="number" inputMode="decimal" style={inputStyle} value={s.resistance}
                      onChange={e => updateSet(exIdx, setIdx, 'resistance', e.target.value)} placeholder="–" />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ ...label, marginBottom: 3 }}>Calories</div>
                    <input type="number" inputMode="numeric" style={inputStyle} value={s.calories}
                      onChange={e => updateSet(exIdx, setIdx, 'calories', e.target.value)} placeholder="–" />
                  </label>
                </div>
                <input style={{ ...inputStyle, marginTop: 8, fontSize: '0.85rem' }} value={s.note}
                  onChange={e => updateSet(exIdx, setIdx, 'note', e.target.value)} placeholder="How did it feel? (optional)" />
              </div>
            ))
          ) : (
            // ── Strength: weight / reps ──────────────────────────────────
            r.sets.map((s, setIdx) => {
              const delta = s.weight === '' ? 0 : Number(s.weight);
              const effective = (bw && bwNum != null && !Number.isNaN(bwNum)) ? roundWeight(Math.max(0, bwNum + delta)) : null;
              return (
              <div key={setIdx} style={{ background: 'var(--grey-50)', borderRadius: 8, padding: 10, marginTop: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr 28px', gap: 8, alignItems: 'end' }}>
                  <div style={{ fontWeight: 700, color: 'var(--grey-700)', paddingBottom: 10 }}>{setIdx + 1}</div>
                  <label style={{ display: 'block' }}>
                    <div style={{ ...label, marginBottom: 3 }}>{bw ? '+ Belt / − Assist (kg)' : 'Weight (kg)'}</div>
                    <input type="number" inputMode="decimal" style={inputStyle} value={s.weight}
                      onChange={e => updateSet(exIdx, setIdx, 'weight', e.target.value)} placeholder={bw ? '0' : '–'} />
                  </label>
                  <label style={{ display: 'block' }}>
                    <div style={{ ...label, marginBottom: 3 }}>Reps</div>
                    <input type="number" inputMode="numeric" style={inputStyle} value={s.reps}
                      onChange={e => updateSet(exIdx, setIdx, 'reps', e.target.value)} placeholder="–" />
                  </label>
                  <button onClick={() => removeSet(exIdx, setIdx)} disabled={r.sets.length <= 1}
                    style={{ color: r.sets.length <= 1 ? 'var(--grey-300)' : 'var(--danger)', fontSize: '1.2rem', lineHeight: 1, paddingBottom: 10 }}
                    aria-label="Remove set">×</button>
                </div>
                {bw && (
                  <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--grey-600)' }}>
                    {delta === 0 ? 'Bodyweight only' : delta > 0 ? `Bodyweight + ${roundWeight(delta)}kg` : `Assisted ${roundWeight(-delta)}kg`}
                    {effective != null && <> · counts as <strong style={{ color: 'var(--green)' }}>{effective}kg</strong></>}
                  </div>
                )}
                <input style={{ ...inputStyle, marginTop: 8, fontSize: '0.85rem' }} value={s.note}
                  onChange={e => updateSet(exIdx, setIdx, 'note', e.target.value)} placeholder="How did it feel? (optional)" />
              </div>
              );
            })
          )}
          <button onClick={() => addSet(exIdx)} style={{ marginTop: 8, color: 'var(--green)', fontWeight: 700, fontSize: '0.82rem' }}>
            {cardio ? '+ Add effort' : '+ Add set'}
          </button>
        </div>
        );
      })}

      {/* Add-exercise picker (edit mode) */}
      {editMode && (
        <div style={{ marginBottom: 16 }}>
          {!pickerOpen ? (
            <button onClick={() => setPickerOpen(true)}
              style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.86rem' }}>+ Add an exercise</button>
          ) : (
            <div style={{ background: 'var(--grey-50)', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: '0.86rem' }}>Add an exercise</div>
                <button onClick={() => { setPickerOpen(false); setPickerSearch(''); }} style={{ color: 'var(--grey-500)', fontSize: '0.78rem', fontWeight: 600 }}>Cancel</button>
              </div>
              <input style={{ ...inputStyle, marginBottom: 10 }} value={pickerSearch} autoFocus
                onChange={e => setPickerSearch(e.target.value)} placeholder="🔍 Search exercises…" />
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {pickerGroups.length === 0 && <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', padding: '6px 0' }}>No matches.</div>}
                {pickerGroups.map(([part, names]) => (
                  <div key={part} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'inline-block', fontSize: '0.62rem', fontWeight: 800, color: '#fff', background: partColor(part), borderRadius: 999, padding: '2px 10px', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{part}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {names.map(nm => (
                        <button key={nm} onClick={() => addExercise(nm)}
                          style={{ fontSize: '0.82rem', fontWeight: 600, color: partColor(part), background: '#fff', border: `1px solid ${partColor(part)}`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                          {nm}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: '0.82rem', color: 'var(--grey-700)', cursor: 'pointer' }}>
            <input type="checkbox" checked={saveToTemplate} onChange={e => setSaveToTemplate(e.target.checked)} style={{ marginTop: 2 }} />
            <span>Also save these changes to my programme (otherwise they only apply to today's session).</span>
          </label>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <div style={label}>Overall session notes (optional)</div>
        <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={notes}
          onChange={e => noteEdit(e.target.value)} placeholder="Anything about the whole session?" />
      </div>

      {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 10 }}>{error}</div>}

      <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 12, textAlign: 'center', lineHeight: 1.4 }}>
        Your progress saves automatically on this device — you can leave and come back.
        Tap finish when the whole workout is done.
      </div>
      <button className="btn btn-primary btn-full" style={{ marginTop: 10 }} onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Finish & save session'}
      </button>
    </div>
  );
}

// Simple read-only list of mobility moves (name + target hold/reps).
function MobilityList({ exercises }) {
  return (
    <div>
      {(exercises || []).map((e, i) => (
        <div key={e.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, padding: '5px 0', borderTop: i ? '1px solid var(--grey-200, #eee)' : 'none' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{e.name}</span>
          <span style={{ fontSize: '0.8rem', color: '#FF6200', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {[e.sets ? `${e.sets}×` : '', e.reps || ''].join(' ').trim() || '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Phase card (view programmes, start logging) ───────────────────────
function PhaseCard({ phase, onLog, onEdit, onDelete, onDeleteProg }) {
  const [openProg, setOpenProg] = useState(null);
  const all = phase.programmes || [];
  const workouts     = all.filter(p => (p.kind || 'workout') !== 'mobility');
  const standaloneMob = all.filter(p => p.kind === 'mobility' && (p.placement || 'standalone') !== 'preworkout');
  const preMob        = all.filter(p => p.kind === 'mobility' && p.placement === 'preworkout');

  const renderProg = (pr, mob) => {
    const open = openProg === pr.id;
    const inProgress = hasDraft(pr.id);
    return (
      <div key={pr.id} style={{ background: mob ? '#FFF3EC' : 'var(--grey-50)', border: mob ? '1px solid #FFD2B8' : 'none', borderRadius: 10, padding: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setOpenProg(open ? null : pr.id)}
            style={{ fontWeight: 800, fontSize: '1rem', textAlign: 'left', flex: 1 }}>
            {mob && <span style={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', background: '#FF6200', borderRadius: 999, padding: '2px 7px', letterSpacing: '0.06em', marginRight: 7, verticalAlign: 'middle' }}>MOBILITY</span>}
            {pr.name} <span style={{ color: 'var(--grey-500)', fontWeight: 500, fontSize: '0.8rem' }}>· {(pr.exercises || []).length} {mob ? 'moves' : 'exercises'}</span>
            {inProgress && <span style={{ marginLeft: 8, fontSize: '0.66rem', fontWeight: 800, color: mob ? '#FF6200' : 'var(--green)', background: mob ? '#FFE3D3' : 'var(--green-pale)', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>In progress</span>}
          </button>
          <button className="btn btn-primary" style={{ padding: '7px 14px', fontSize: '0.82rem', ...(mob ? { background: '#FF6200', borderColor: '#FF6200' } : {}) }}
            onClick={() => onLog(phase, pr)}>{inProgress ? 'Continue' : (mob ? 'Start session' : 'Start workout')}</button>
        </div>
        {open && (
          <div style={{ marginTop: 10, borderTop: '1px solid var(--grey-200, #eee)', paddingTop: 10 }}>
            {(pr.exercises || []).length === 0
              ? <div style={{ color: 'var(--grey-500)', fontSize: '0.82rem' }}>{mob ? 'No moves.' : 'No exercises.'}</div>
              : (mob ? <MobilityList exercises={pr.exercises} /> : <ExerciseGroups exercises={pr.exercises} />)}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button onClick={() => onDeleteProg(phase, pr)}
                style={{ color: 'var(--grey-500)', fontSize: '0.72rem', fontWeight: 600 }}>
                Delete this {mob ? 'session' : 'workout'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>{phase.name}</div>
          <div style={{ color: 'var(--grey-500)', fontSize: '0.78rem' }}>
            {workouts.length} {workouts.length === 1 ? 'workout' : 'workouts'}
            {standaloneMob.length > 0 && ` · ${standaloneMob.length} mobility`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
          <button onClick={() => onEdit(phase)} style={{ color: 'var(--green)', fontSize: '0.75rem', fontWeight: 700 }}>Edit</button>
          <button onClick={() => onDelete(phase)} style={{ color: 'var(--grey-500)', fontSize: '0.75rem', fontWeight: 600 }}>Remove</button>
        </div>
      </div>

      {all.length === 0 && (
        <div style={{ color: 'var(--grey-500)', fontSize: '0.88rem' }}>No workouts in this phase yet.</div>
      )}

      {standaloneMob.map(pr => renderProg(pr, true))}
      {workouts.map(pr => renderProg(pr, false))}

      {preMob.length > 0 && (
        <div style={{ background: '#FFF3EC', border: '1px dashed #FFC2A0', borderRadius: 10, padding: '10px 12px', fontSize: '0.78rem', color: 'var(--grey-600, #555)', lineHeight: 1.45 }}>
          🤸 <strong style={{ color: '#FF6200' }}>{preMob.map(p => p.name).join(', ')}</strong> {preMob.length === 1 ? 'shows' : 'show'} as a warm-up before every workout above — tick it off when you log a session.
        </div>
      )}
    </div>
  );
}

// ── Programme exercise codes (A1, A2, B1 …) ────────────────────────────
// Codes follow a strict superset scheme: a letter = one superset group,
// the number = position within it. After A1 the only valid next codes are
// A2 (same group) or B1 (new group); after C4 → C5 or D1. We store each
// exercise's relation to the one above it ('base' | 'same' | 'new') and
// derive the real code, so a code can never be mistyped.
function parseCode(code) {
  const m = String(code || '').trim().match(/^([A-Za-z]+)\s*0*(\d+)$/);
  return m ? { letter: m[1].toUpperCase(), num: parseInt(m[2], 10) } : null;
}
function nextLetter(letter) {
  const arr = String(letter || 'A').toUpperCase().split('');
  let i = arr.length - 1;
  while (i >= 0) {
    if (arr[i] === 'Z') { arr[i] = 'A'; i--; }
    else { arr[i] = String.fromCharCode(arr[i].charCodeAt(0) + 1); return arr.join(''); }
  }
  return 'A' + arr.join('');
}
// [sameGroupCode, newGroupCode] for the exercise placed after `prevCode`.
function nextCodeOptions(prevCode) {
  const p = parseCode(prevCode);
  if (!p) return ['A1', 'A1'];
  return [`${p.letter}${p.num + 1}`, `${nextLetter(p.letter)}1`];
}
// Assign codes down a list from each exercise's rel ('base'|'same'|'new').
function computeCodes(exercises) {
  let letter = 'A', num = 1;
  return (exercises || []).map((e, i) => {
    if (i === 0) { letter = 'A'; num = 1; }
    else if (e.rel === 'new') { letter = nextLetter(letter); num = 1; }
    else { num += 1; }
    return { ...e, code: `${letter}${num}` };
  });
}
// Infer each exercise's rel from its stored code (for loading saved phases).
function withRels(exercises) {
  let prev = null;
  return (exercises || []).map((e, i) => {
    const cur = parseCode(e.code);
    let rel;
    if (i === 0) rel = 'base';
    else if (prev && cur && cur.letter === prev.letter && cur.num === prev.num + 1) rel = 'same';
    else rel = 'new';
    prev = cur || prev;
    return { ...e, rel };
  });
}

// ── Build-your-own phase ──────────────────────────────────────────────
const blankExercise = () => ({ code: '', name: '', sets: '', reps: '', tempo: '', rest_seconds: '', notes: '', target_weight: '', t_time: '', t_distance: '', t_resistance: '', t_calories: '', _custom: false, rel: 'same' });
const blankProgramme = (n) => ({ name: n, kind: 'workout', placement: 'standalone', exercises: [blankExercise()] });
// A flexibility / mobility block. Defaults to its own session; the builder
// toggle can switch it to a pre-workout block shown before every workout.
const blankMobilityProgramme = (n) => ({ name: n, kind: 'mobility', placement: 'standalone', exercises: [blankExercise()] });

// Map a phase from get_my_training into the editable form shape. Mobility
// programmes are handled separately (the blue "Stretching & Mobility" box),
// so only strength/cardio workouts become programme cards here.
function phaseToForm(ph) {
  const progs = (ph.programmes || []).filter(pr => pr.kind !== 'mobility').map(pr => ({
    id: pr.id || null,
    name: pr.name || '',
    kind: 'workout',
    placement: 'standalone',
    exercises: (pr.exercises || []).length
      ? withRels(pr.exercises.map(e => ({
          code: e.code || '',
          name: e.name || '',
          sets: e.sets == null ? '' : String(e.sets),
          reps: e.reps || '',
          tempo: e.tempo || '',
          rest_seconds: e.rest_seconds == null ? '' : String(e.rest_seconds),
          notes: e.notes || '',
          target_weight: e.target_weight == null ? '' : String(e.target_weight),
          t_time: e.t_time == null ? '' : String(e.t_time),
          t_distance: e.t_distance == null ? '' : String(e.t_distance),
          t_resistance: e.t_resistance == null ? '' : String(e.t_resistance),
          t_calories: e.t_calories == null ? '' : String(e.t_calories),
          _custom: false,
        })))
      : [blankExercise()],
  }));
  return progs.length ? progs : [blankProgramme('1A')];
}

// The single mobility programme on a phase, if any.
function phaseMobProgramme(ph) {
  return ((ph && ph.programmes) || []).find(pr => pr.kind === 'mobility') || null;
}
// Stretch list for the blue editor box — from a mobility programme if present,
// otherwise the legacy phase-level mobility jsonb.
function phaseToMobility(ph) {
  if (!ph) return [];
  const mp = phaseMobProgramme(ph);
  if (mp && (mp.exercises || []).length) {
    return mp.exercises.map(e => ({ name: e.name || '', mode: 'single', amount: e.reps || '', left: '', right: '' }));
  }
  return Array.isArray(ph.mobility) ? ph.mobility : [];
}

// ── Searchable, colour-coded exercise picker ──────────────────────────
// A full-screen overlay with a search box, body-part tabs, and the
// exercises shown as colour-coded chips. Used when choosing an exercise in
// the phase builder (and anywhere else a richer picker helps).
function ExercisePickerModal({ catalogue, value, onPick, onClose, allowCustom = true }) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('All');

  const tabs = useMemo(() => {
    const set = new Set((catalogue || []).map(c => c.body_part).filter(Boolean));
    const known = BODY_PART_ORDER.filter(p => set.has(p));
    const extra = Array.from(set).filter(p => !BODY_PART_ORDER.includes(p)).sort();
    return ['All', ...known, ...extra];
  }, [catalogue]);

  const groups = useMemo(() => {
    const query = q.trim().toLowerCase();
    const g = {};
    for (const e of (catalogue || [])) {
      if (!e.name) continue;
      if (tab !== 'All' && e.body_part !== tab) continue;
      if (query && !e.name.toLowerCase().includes(query)) continue;
      (g[e.body_part] = g[e.body_part] || []).push(e.name);
    }
    const ordered = Object.keys(g).sort((a, b) => {
      const ia = BODY_PART_ORDER.indexOf(a), ib = BODY_PART_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1; if (ib === -1) return -1; return ia - ib;
    });
    return ordered.map(p => [p, g[p].sort((a, b) => a.localeCompare(b))]);
  }, [catalogue, q, tab]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 520, maxHeight: '88vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 24px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 16px 8px', borderBottom: '1px solid var(--grey-200, #eee)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800, fontSize: '1rem' }}>Choose an exercise</div>
            <button onClick={onClose} style={{ color: 'var(--grey-500)', fontSize: '1.1rem', lineHeight: 1 }} aria-label="Close">✕</button>
          </div>
          {/* No autoFocus: this picker is a bottom-sheet, and auto-popping the
              mobile keyboard covers the exercise list below it (the list then
              looks empty — it's just hidden behind the keyboard). The member
              taps the field only when they actually want to filter. */}
          <input style={{ ...inputStyle }} value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search exercises…" />
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginTop: 10, paddingBottom: 4 }}>
            {tabs.map(t => {
              const active = t === tab;
              const c = t === 'All' ? 'var(--green)' : partColor(t);
              return (
                <button key={t} onClick={() => setTab(t)}
                  style={{ flexShrink: 0, fontSize: '0.74rem', fontWeight: 700, padding: '5px 12px', borderRadius: 999, whiteSpace: 'nowrap', cursor: 'pointer', color: active ? '#fff' : c, background: active ? c : '#fff', border: `1px solid ${c}` }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ overflowY: 'auto', padding: '12px 16px', flex: 1 }}>
          {groups.length === 0 && <div style={{ color: 'var(--grey-500)', fontSize: '0.88rem', padding: '8px 0' }}>No exercises match “{q}”.</div>}
          {groups.map(([part, names]) => (
            <div key={part} style={{ marginBottom: 14 }}>
              <div style={{ display: 'inline-block', fontSize: '0.62rem', fontWeight: 800, color: '#fff', background: partColor(part), borderRadius: 999, padding: '2px 10px', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>{part}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {names.map(nm => {
                  const sel = value && nm === value;
                  return (
                    <button key={nm} onClick={() => onPick(nm)}
                      style={{ fontSize: '0.84rem', fontWeight: 600, padding: '7px 11px', borderRadius: 8, cursor: 'pointer', color: sel ? '#fff' : partColor(part), background: sel ? partColor(part) : '#fff', border: `1px solid ${partColor(part)}` }}>
                      {sel ? '✓ ' : ''}{nm}
                    </button>
                  );
                })}
                {allowCustom && (
                  <button key="__add__" onClick={() => onPick('__custom__', part)}
                    style={{ fontSize: '0.84rem', fontWeight: 700, padding: '7px 11px', borderRadius: 8, cursor: 'pointer', color: partColor(part), background: '#fff', border: `1px dashed ${partColor(part)}` }}>
                    + Custom {part}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {allowCustom && (
          <div style={{ padding: '10px 16px 16px', borderTop: '1px solid var(--grey-200, #eee)' }}>
            <button onClick={() => onPick('__custom__')} className="btn btn-secondary btn-full" style={{ fontSize: '0.86rem' }}>
              ✏️ Type a custom exercise instead
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BuildPhase({ existing, onDone, onCancel }) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing ? (existing.name || '') : '');
  const [mobility, setMobility] = useState(() => phaseToMobility(existing));
  const [mobInProgramme, setMobInProgramme] = useState(() => {
    const mp = phaseMobProgramme(existing);
    return mp ? mp.placement === 'preworkout' : true;
  });
  const [programmes, setProgrammes] = useState(() => (existing ? phaseToForm(existing) : [blankProgramme('1A')]));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Shared exercise library (powers the dropdowns)
  const [catalogue, setCatalogue] = useState([]);
  const [catLoaded, setCatLoaded] = useState(false);
  const [reconciled, setReconciled] = useState(!existing);
  const [isAdmin, setIsAdmin] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [newExName, setNewExName] = useState('');
  const [newExPart, setNewExPart] = useState('Chest');
  const [libErr, setLibErr] = useState('');
  const [libSaving, setLibSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // { pi, ei } row currently picking

  function loadCatalogue() {
    return supabase.rpc('get_exercise_catalogue').then(({ data }) => { setCatalogue(data || []); setCatLoaded(true); });
  }
  useEffect(() => {
    loadCatalogue();
    supabase.rpc('get_my_profile').then(({ data }) => {
      const p = Array.isArray(data) ? data[0] : data;
      if (p && p.is_admin) setIsAdmin(true);
    }).catch(() => {});
  }, []);

  const nameSet = useMemo(() => new Set(catalogue.map(c => c.name)), [catalogue]);
  // Name → body part, so the builder can show cardio-specific target boxes.
  const partByName = useMemo(() => {
    const m = {};
    for (const c of catalogue) if (c.name) m[c.name] = c.body_part;
    return m;
  }, [catalogue]);

  // When editing, once the library has loaded, flag any exercise whose name
  // isn't in the library as "custom" so it shows as an editable text field
  // (rather than an empty dropdown). Runs once.
  useEffect(() => {
    if (reconciled || !catLoaded) return;
    setProgrammes(prev => prev.map(p => ({
      ...p,
      exercises: p.exercises.map(e => ({ ...e, _custom: e.name ? !nameSet.has(e.name) : false })),
    })));
    setReconciled(true);
  }, [reconciled, catLoaded, nameSet]);

  async function addToLibrary() {
    setLibErr('');
    if (!newExName.trim()) { setLibErr('Enter an exercise name.'); return; }
    setLibSaving(true);
    const { error: e } = await supabase.rpc('add_exercise_to_catalogue', {
      p_name: newExName.trim(), p_body_part: newExPart,
    });
    if (e) { setLibSaving(false); setLibErr(e.message || 'Could not add exercise.'); return; }
    await loadCatalogue();
    setLibSaving(false);
    setNewExName('');
  }

  function patchEx(pi, ei, patch) {
    setProgrammes(prev => prev.map((p, i) => {
      if (i !== pi) return p;
      return { ...p, exercises: p.exercises.map((e, j) => (j === ei ? { ...e, ...patch } : e)) };
    }));
  }
  function updateProg(pi, field, value) {
    setProgrammes(prev => prev.map((p, i) => (i === pi ? { ...p, [field]: value } : p)));
  }
  function addProg() {
    setProgrammes(prev => [...prev, blankProgramme(`${prev.length + 1} new`)]);
  }
  function removeProg(pi) { setProgrammes(prev => prev.filter((_, i) => i !== pi)); }
  function updateEx(pi, ei, field, value) {
    setProgrammes(prev => prev.map((p, i) => {
      if (i !== pi) return p;
      return { ...p, exercises: p.exercises.map((e, j) => (j === ei ? { ...e, [field]: value } : e)) };
    }));
  }
  function addEx(pi) {
    setProgrammes(prev => prev.map((p, i) => i === pi ? { ...p, exercises: [...p.exercises, blankExercise()] } : p));
  }
  function removeEx(pi, ei) {
    setProgrammes(prev => prev.map((p, i) => i === pi ? { ...p, exercises: p.exercises.filter((_, j) => j !== ei) } : p));
  }

  async function save() {
    setSaving(true); setError('');
    if (!name.trim()) { setError('Give your phase a name.'); setSaving(false); return; }
    // Workout programmes from the cards.
    const workoutProgs = programmes.map(p => {
      const coded = computeCodes(p.exercises.filter(e => e.name.trim()));
      return {
        id: p.id || null,
        name: p.name.trim() || 'Workout',
        kind: 'workout',
        placement: 'standalone',
        exercises: coded.map(e => ({
          code: e.code || null,
          name: e.name.trim(),
          sets: e.sets === '' ? null : Number(e.sets),
          reps: (e.reps || '').trim() || null,
          tempo: (e.tempo || '').trim() || null,
          rest_seconds: e.rest_seconds === '' ? null : Number(e.rest_seconds),
          notes: (e.notes || '').trim() || null,
          // Prescribed load — preloads guided mode on a first-ever run.
          target_weight: (String(e.target_weight ?? '')).trim() === '' ? null : Number(e.target_weight),
          // Cardio targets (null unless set) — mirror the log screen fields.
          t_time: (e.t_time || '') === '' ? null : Number(e.t_time),
          t_distance: (e.t_distance || '') === '' ? null : Number(e.t_distance),
          t_resistance: (e.t_resistance || '') === '' ? null : Number(e.t_resistance),
          t_calories: (e.t_calories || '') === '' ? null : Number(e.t_calories),
        })),
      };
    });

    // The blue "Stretching & Mobility" box becomes a single mobility
    // programme — placed before every workout, or as its own session.
    const mobEx = mobility
      .filter(m => (m.name || '').trim())
      .map(m => ({
        code: null,
        name: m.name.trim(),
        sets: null,
        reps: m.mode === 'split'
          ? [m.left ? `L ${m.left}` : '', m.right ? `R ${m.right}` : ''].filter(Boolean).join(' / ') || null
          : ((m.amount || '').trim() || null),
        tempo: null, rest_seconds: null, notes: null,
      }));
    const mobProgs = mobEx.length
      ? [{ name: 'Stretching & Mobility', kind: 'mobility', placement: mobInProgramme ? 'preworkout' : 'standalone', exercises: mobEx }]
      : [];

    const payload = {
      name: name.trim(),
      mobility: [], // mobility now lives as a programme, not phase-level jsonb
      programmes: [...workoutProgs, ...mobProgs],
    };
    const { error: rpcErr } = isEdit
      ? await supabase.rpc('update_my_phase', { p_phase_id: existing.id, payload })
      : await supabase.rpc('create_my_phase', { payload });
    setSaving(false);
    if (rpcErr) { setError(rpcErr.message || 'Could not save phase.'); return; }
    onDone();
  }

  const half = { ...inputStyle, padding: '8px 10px', fontSize: '0.9rem' };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>{isEdit ? 'Edit phase' : 'Build a phase'}</div>
        <button className="btn btn-secondary" style={{ padding: '8px 14px' }} onClick={onCancel}>Cancel</button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={label}>Phase name</div>
        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Phase 1" />
      </div>

      <MobilityEditor items={mobility} onChange={setMobility}
        inProgramme={mobInProgramme} onInProgrammeChange={setMobInProgramme} />

      {isAdmin && (
        <div style={{ background: 'var(--grey-50)', borderRadius: 10, padding: 12, marginBottom: 12, border: '1px dashed var(--grey-300)' }}>
          <button onClick={() => setLibOpen(o => !o)} style={{ fontWeight: 700, fontSize: '0.86rem', color: 'var(--green)' }}>
            {libOpen ? '− Hide exercise library' : '+ Add an exercise to the library (staff)'}
          </button>
          {libOpen && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginBottom: 8 }}>
                New exercises appear in the dropdown menus for everyone.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8 }}>
                <input style={{ ...inputStyle, padding: '8px 10px', fontSize: '0.9rem' }} value={newExName}
                  onChange={e => setNewExName(e.target.value)} placeholder="Exercise name" />
                <select style={{ ...inputStyle, padding: '8px 10px', fontSize: '0.9rem' }} value={newExPart}
                  onChange={e => setNewExPart(e.target.value)}>
                  {BODY_PART_ORDER.map(bp => <option key={bp} value={bp}>{bp}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              {libErr && <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 6 }}>{libErr}</div>}
              <button className="btn btn-primary" style={{ marginTop: 8, padding: '8px 16px', fontSize: '0.84rem' }}
                onClick={addToLibrary} disabled={libSaving}>
                {libSaving ? 'Adding…' : 'Add to library'}
              </button>
            </div>
          )}
        </div>
      )}

      {programmes.map((p, pi) => {
        const isMob = p.kind === 'mobility';
        const coded = isMob ? p.exercises : computeCodes(p.exercises);
        return (
        <div key={pi} style={{ background: isMob ? '#FFF3EC' : 'var(--grey-50)', borderRadius: 10, padding: 12, marginBottom: 12, border: isMob ? '1px solid #FFD2B8' : 'none' }}>
          {isMob && (
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#C24E00', marginBottom: 8 }}>🤸 Stretching &amp; Mobility</div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input style={{ ...inputStyle, fontWeight: 700 }} value={p.name}
              onChange={e => updateProg(pi, 'name', e.target.value)} placeholder={isMob ? 'Name (optional, e.g. Hip opener)' : 'Workout (e.g. 1A)'} />
            {programmes.length > 1 && (
              <button onClick={() => removeProg(pi)} style={{ color: 'var(--danger)', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Remove</button>
            )}
          </div>

          {/* Mobility: one choice — part of the programme, or its own session */}
          {isMob && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: '0.82rem', color: 'var(--grey-700)' }}>
              <input type="checkbox" checked={p.placement === 'preworkout'}
                onChange={ev => updateProg(pi, 'placement', ev.target.checked ? 'preworkout' : 'standalone')}
                style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                <strong>Add to the programme</strong> — shows before every workout in this phase.
                <span style={{ color: 'var(--grey-500)' }}> Leave unticked for its own standalone session.</span>
              </span>
            </label>
          )}

          {p.exercises.map((e, ei) => {
            const rowCode = coded[ei] ? coded[ei].code : 'A1';
            const opts = ei === 0 ? ['A1', 'A1'] : nextCodeOptions((coded[ei - 1] || {}).code);
            const namePicker = e._custom ? (
              <input style={half} value={e.name} onChange={ev => updateEx(pi, ei, 'name', ev.target.value)} placeholder={isMob ? 'Stretch / mobilisation' : 'Custom exercise'} />
            ) : (() => {
              const chosen = nameSet.has(e.name) ? e.name : '';
              const part = chosen ? (catalogue.find(c => c.name === chosen) || {}).body_part : null;
              return (
                <button type="button" onClick={() => setPickerFor({ pi, ei })}
                  style={{ ...half, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, borderColor: part ? partColor(part) : 'var(--grey-300)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                    {part && <span style={{ width: 8, height: 8, borderRadius: 999, background: partColor(part), flexShrink: 0 }} />}
                    <span style={{ color: chosen ? 'var(--grey-800, #222)' : 'var(--grey-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {chosen || (isMob ? 'Choose a stretch…' : 'Choose exercise…')}
                    </span>
                  </span>
                  <span style={{ color: 'var(--grey-400)', flexShrink: 0 }}>▾</span>
                </button>
              );
            })();
            return (
            <div key={ei} style={{ background: '#fff', border: '1px solid var(--grey-200, #eee)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMob ? '1fr 22px' : '96px 1fr 22px', gap: 6, marginBottom: 6 }}>
                {!isMob && (ei === 0 ? (
                  <div style={{ ...half, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--grey-700)', background: 'var(--grey-50)' }} title="First exercise in this workout">{rowCode}</div>
                ) : (
                  <select style={{ ...half, padding: '8px 6px', fontWeight: 700, cursor: 'pointer' }}
                    value={e.rel === 'new' ? 'new' : 'same'} onChange={ev => updateEx(pi, ei, 'rel', ev.target.value)}>
                    <option value="same">{opts[0]} · same</option>
                    <option value="new">{opts[1]} · new</option>
                  </select>
                ))}
                {namePicker}
                <button onClick={() => removeEx(pi, ei)} disabled={p.exercises.length <= 1}
                  style={{ color: p.exercises.length <= 1 ? 'var(--grey-300)' : 'var(--danger)', fontSize: '1.1rem' }}>×</button>
              </div>
              {e._custom && (
                <button onClick={() => patchEx(pi, ei, { _custom: false, name: '' })}
                  style={{ color: 'var(--grey-500)', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'underline', marginBottom: 6 }}>
                  ↩ Pick from list instead
                </button>
              )}
              {isMob ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 6 }}>
                  <input style={half} type="number" inputMode="numeric" value={e.sets} onChange={ev => updateEx(pi, ei, 'sets', ev.target.value)} placeholder="Rounds" />
                  <input style={half} value={e.reps} onChange={ev => updateEx(pi, ei, 'reps', ev.target.value)} placeholder="Hold / reps (e.g. 45s)" />
                </div>
              ) : (partByName[e.name] === 'Cardio') ? (
                // Cardio: efforts + the same target boxes the member logs.
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={{ ...half, gridColumn: '1 / -1' }} type="number" inputMode="numeric" value={e.sets} onChange={ev => updateEx(pi, ei, 'sets', ev.target.value)} placeholder="Efforts (rounds)" />
                  <input style={half} type="number" inputMode="decimal" value={e.t_time} onChange={ev => updateEx(pi, ei, 't_time', ev.target.value)} placeholder="Time (min)" />
                  <input style={half} type="number" inputMode="decimal" value={e.t_distance} onChange={ev => updateEx(pi, ei, 't_distance', ev.target.value)} placeholder="Distance" />
                  <input style={half} type="number" inputMode="decimal" value={e.t_resistance} onChange={ev => updateEx(pi, ei, 't_resistance', ev.target.value)} placeholder="Resistance" />
                  <input style={half} type="number" inputMode="numeric" value={e.t_calories} onChange={ev => updateEx(pi, ei, 't_calories', ev.target.value)} placeholder="Calories" />
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 6 }}>
                  <input style={half} type="number" inputMode="numeric" value={e.sets} onChange={ev => updateEx(pi, ei, 'sets', ev.target.value)} placeholder="Sets" />
                  <input style={half} value={e.reps} onChange={ev => updateEx(pi, ei, 'reps', ev.target.value)} placeholder="Reps" />
                  <input style={half} value={e.tempo} onChange={ev => updateEx(pi, ei, 'tempo', ev.target.value)} placeholder="Tempo" />
                  <input style={half} type="number" inputMode="numeric" value={e.rest_seconds} onChange={ev => updateEx(pi, ei, 'rest_seconds', ev.target.value)} placeholder="Rest s" />
                  <input style={half} type="number" inputMode="decimal" value={e.target_weight} onChange={ev => updateEx(pi, ei, 'target_weight', ev.target.value)} placeholder="Target kg" />
                </div>
              )}
              <input style={{ ...half, marginTop: 6 }} value={e.notes} onChange={ev => updateEx(pi, ei, 'notes', ev.target.value)} placeholder="Notes (optional)" />
            </div>
            );
          })}
          <button onClick={() => addEx(pi)} style={{ color: isMob ? '#FF6200' : 'var(--green)', fontWeight: 700, fontSize: '0.82rem', marginTop: 2 }}>+ Add {isMob ? 'stretch' : 'exercise'}</button>
        </div>
        );
      })}

      <div style={{ marginBottom: 12 }}>
        <button onClick={addProg} style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.86rem' }}>+ Add workout</button>
      </div>

      <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginBottom: 10 }}>
        Tip: codes set themselves. Choose <strong>same</strong> to pair an exercise with the one above it as a superset (A1 &amp; A2), or <strong>new</strong> to start a fresh group (B1).
      </div>

      {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 10 }}>{error}</div>}
      <button className="btn btn-primary btn-full" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Save phase')}
      </button>

      {pickerFor && (
        <ExercisePickerModal
          catalogue={catalogue}
          value={(() => {
            const row = programmes[pickerFor.pi] && programmes[pickerFor.pi].exercises[pickerFor.ei];
            return row && nameSet.has(row.name) ? row.name : '';
          })()}
          onClose={() => setPickerFor(null)}
          onPick={async (name, part) => {
            // "+ Custom <part>" — add it to the library under that body part so
            // it lands in that part of the list from now on, then select it.
            if (name === '__custom__' && part) {
              const typed = (typeof window !== 'undefined' && window.prompt)
                ? window.prompt(`New ${part} exercise name:`) : '';
              const nm = (typed || '').trim();
              if (!nm) { setPickerFor(null); return; }
              const { pi, ei } = pickerFor;
              try { await supabase.rpc('add_exercise_to_catalogue', { p_name: nm, p_body_part: part }); } catch (_) {}
              await loadCatalogue();
              patchEx(pi, ei, { _custom: false, name: nm });
              setPickerFor(null);
              return;
            }
            if (name === '__custom__') patchEx(pickerFor.pi, pickerFor.ei, { _custom: true, name: '' });
            else patchEx(pickerFor.pi, pickerFor.ei, { _custom: false, name });
            setPickerFor(null);
          }}
        />
      )}
    </div>
  );
}

// ── History ────────────────────────────────────────────────────────────
// Total session volume = Σ weight × reps over every weighted set.
function sessVol(s) {
  return (s.sets || []).reduce((sum, set) => {
    if (set.weight == null || set.reps == null) return sum;
    const w = Number(set.weight), r = Number(set.reps);
    if (Number.isNaN(w) || Number.isNaN(r)) return sum;
    return sum + w * r;
  }, 0);
}

// Running total of volume lifted so far this calendar month, with a
// comparison against the same point last month isn't needed — we compare
// to last month's full total as a simple momentum cue.
function MonthVolume({ sessions }) {
  const now = new Date();
  const curY = now.getFullYear(), curM = now.getMonth();
  const prevDate = new Date(curY, curM - 1, 1);
  const prevY = prevDate.getFullYear(), prevM = prevDate.getMonth();

  let thisTotal = 0, thisCount = 0, lastTotal = 0;
  for (const s of (sessions || [])) {
    let d;
    try { d = parseISO(s.performed_on); } catch (_) { continue; }
    if (!d || Number.isNaN(d.getTime())) continue;
    const vol = sessVol(s);
    if (vol <= 0) continue;
    if (d.getFullYear() === curY && d.getMonth() === curM) { thisTotal += vol; thisCount += 1; }
    else if (d.getFullYear() === prevY && d.getMonth() === prevM) { lastTotal += vol; }
  }

  const deltaPct = lastTotal > 0 ? Math.round(((thisTotal - lastTotal) / lastTotal) * 100) : null;

  return (
    <div style={{ ...card, background: 'linear-gradient(135deg, #1A1A1A, #000)', color: '#fff', border: 'none' }}>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FF8033' }}>
        {format(now, 'MMMM')} · total volume lifted
      </div>
      <div style={{ fontSize: '2.1rem', fontWeight: 900, lineHeight: 1.1, marginTop: 4, color: '#fff' }}>
        {Math.round(thisTotal).toLocaleString()}<span style={{ fontSize: '1.05rem', fontWeight: 800, marginLeft: 4, color: '#FF6200' }}>kg</span>
      </div>
      <div style={{ fontSize: '0.82rem', color: '#bbb', marginTop: 2 }}>
        {thisCount === 0
          ? 'No workouts logged yet this month — get one in!'
          : `across ${thisCount} workout${thisCount === 1 ? '' : 's'} so far`}
      </div>
      {deltaPct != null && thisCount > 0 && (
        <div style={{ fontSize: '0.78rem', fontWeight: 700, marginTop: 10, display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: '#FF6200', color: '#fff' }}>
          {deltaPct >= 0 ? '▲' : '▼'} {Math.abs(deltaPct)}% vs {format(prevDate, 'MMMM')} ({Math.round(lastTotal).toLocaleString()} kg)
        </div>
      )}
    </div>
  );
}

// Classify a logged set so we know how to show it and whether it's editable.
function logSetKind(set) {
  if (set.hold_seconds != null && set.weight == null && set.reps == null) return 'mob';
  const hasCardio = set.duration_min != null || set.distance != null
    || set.resistance != null || set.calories != null;
  if (hasCardio && set.weight == null && set.reps == null) return 'cardio';
  return 'strength';
}

const editSetInput = {
  width: 60, padding: '6px 8px', border: '1px solid var(--grey-300)',
  borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box',
};

function SessionCard({ s, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const byExercise = {};
  const orderKeys = [];
  (s.sets || []).forEach(set => {
    const key = (set.exercise_code ? set.exercise_code + ' ' : '') + (set.exercise_name || 'Exercise');
    if (!byExercise[key]) { byExercise[key] = []; orderKeys.push(key); }
    byExercise[key].push(set);
  });
  let dateStr = s.performed_on;
  try { dateStr = format(parseISO(s.performed_on), 'EEE d MMM yyyy'); } catch (_) {}

  // Total volume lifted = sum of weight × reps across all weighted sets.
  const totalVolume = (s.sets || []).reduce((sum, set) => {
    const w = Number(set.weight), r = Number(set.reps);
    if (!w || Number.isNaN(w) || !r || Number.isNaN(r)) return sum;
    return sum + w * r;
  }, 0);
  const volLabel = totalVolume > 0 ? `${Math.round(totalVolume).toLocaleString()} kg` : null;

  const hasStrength = (s.sets || []).some(set => logSetKind(set) === 'strength');

  function startEdit() {
    const d = {};
    (s.sets || []).forEach(set => {
      if (logSetKind(set) === 'strength')
        d[set.id] = {
          weight: set.weight == null ? '' : String(set.weight),
          reps: set.reps == null ? '' : String(set.reps),
        };
    });
    setDraft(d);
    setEditing(true);
  }
  function setField(id, field, val) {
    setDraft(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  }
  async function saveEdit() {
    setSaving(true);
    const sets = Object.entries(draft).map(([id, v]) => ({ id, weight: v.weight, reps: v.reps }));
    const { error } = await supabase.rpc('update_my_set_logs', { payload: { sets } });
    setSaving(false);
    if (error) { window.alert(`Couldn't save your changes: ${error.message}`); return; }
    setEditing(false);
    if (onChange) onChange();
  }

  async function share() {
    const lines = [`💪 ${s.programme_name || 'Workout'}${s.phase_name ? ' · ' + s.phase_name : ''} — ${dateStr}`];
    if (volLabel) lines.push(`Total volume lifted: ${volLabel}`);
    orderKeys.forEach(exName => {
      const sets = byExercise[exName].map(set => {
        if (set.hold_seconds != null && set.weight == null && set.reps == null)
          return Number(set.hold_seconds) > 0 ? `${set.hold_seconds}s` : 'done';
        const cardioBits = [];
        if (set.duration_min != null) cardioBits.push(`${set.duration_min} min`);
        if (set.distance != null) cardioBits.push(`${set.distance} dist`);
        if (set.resistance != null) cardioBits.push(`res ${set.resistance}`);
        if (set.calories != null) cardioBits.push(`${set.calories} cal`);
        if (cardioBits.length && set.weight == null && set.reps == null) return cardioBits.join(' ');
        return `${set.weight != null ? set.weight + 'kg' : 'BW'}×${set.reps != null ? set.reps : '–'}`;
      }).join(', ');
      lines.push(`${exName}: ${sets}`);
    });
    lines.push('— Team Breakthrough');
    const text = lines.join('\n');
    try {
      if (navigator.share) { await navigator.share({ title: 'My workout', text }); return; }
      await navigator.clipboard.writeText(text);
      alert('Workout copied — paste it into your story or post.');
    } catch (_) { /* user cancelled */ }
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontWeight: 800 }}>
          {s.programme_name || 'Session'}
          {s.phase_name && <span style={{ color: 'var(--grey-500)', fontWeight: 500, fontSize: '0.8rem' }}> · {s.phase_name}</span>}
        </div>
        <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>{dateStr}</div>
      </div>

      {volLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 12px', background: 'var(--green-pale)', borderRadius: 8 }}>
          <span style={{ fontSize: '1.1rem' }}>🏋️</span>
          <div>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--green)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Total volume lifted</div>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--grey-800, #222)' }}>{volLabel}</div>
          </div>
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--grey-600, #555)' }}>
          Fix any weight or reps you mistyped, then tap Save changes.
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        {orderKeys.map(exName => (
          <div key={exName} style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{exName}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {byExercise[exName].map(set => {
                const kind = logSetKind(set);
                const isMobSet = kind === 'mob';

                if (editing && kind === 'strength') {
                  const dv = draft[set.id] || { weight: '', reps: '' };
                  return (
                    <div key={set.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" inputMode="decimal" value={dv.weight}
                        onChange={e => setField(set.id, 'weight', e.target.value)}
                        placeholder="kg" style={editSetInput} />
                      <span style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>kg ×</span>
                      <input type="number" inputMode="numeric" value={dv.reps}
                        onChange={e => setField(set.id, 'reps', e.target.value)}
                        placeholder="reps" style={editSetInput} />
                      <span style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>reps</span>
                    </div>
                  );
                }

                const cardioBits = [];
                if (set.duration_min != null) cardioBits.push(`${set.duration_min} min`);
                if (set.distance != null) cardioBits.push(`${set.distance} dist`);
                if (set.resistance != null) cardioBits.push(`res ${set.resistance}`);
                if (set.calories != null) cardioBits.push(`${set.calories} cal`);
                const chip = isMobSet
                  ? (Number(set.hold_seconds) > 0 ? `⏱ ${set.hold_seconds}s` : '✓ done')
                  : kind === 'cardio'
                    ? cardioBits.join(' · ')
                    : `${set.weight != null ? `${set.weight}kg` : 'BW'} × ${set.reps != null ? set.reps : '–'}`;
                return (
                  <div key={set.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: '0.78rem', background: isMobSet ? '#FFE3D3' : 'var(--grey-100)', borderRadius: 6, padding: '3px 8px', color: isMobSet ? '#C24E00' : 'var(--grey-700)', whiteSpace: 'nowrap' }}>
                      {chip}
                    </span>
                    {set.note && <span style={{ fontSize: '0.74rem', color: 'var(--grey-500)', fontStyle: 'italic' }}>“{set.note}”</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {s.notes && <div style={{ marginTop: 6, fontSize: '0.84rem', color: 'var(--grey-700)', fontStyle: 'italic' }}>{s.notes}</div>}
      {s.feedback && s.feedback.note && (
        <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--green-pale)', borderRadius: 8, fontSize: '0.86rem', color: 'var(--grey-700)', lineHeight: 1.45 }}>
          <span style={{ fontWeight: 800, color: 'var(--green)' }}>💬 Coach feedback</span>
          <div style={{ marginTop: 3 }}>{s.feedback.note}</div>
        </div>
      )}

      {editing ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }}
            onClick={() => setEditing(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" style={{ flex: 1 }}
            disabled={saving} onClick={saveEdit}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {hasStrength && (
            <button type="button" onClick={startEdit}
              style={{ flex: '0 0 auto', padding: '9px 16px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--grey-300)', color: 'var(--grey-700)', background: '#fff', cursor: 'pointer' }}>
              ✎ Edit
            </button>
          )}
          <button type="button" onClick={share}
            style={{ flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--green)', color: 'var(--green)', background: '#fff', cursor: 'pointer' }}>
            📲 Share this workout
          </button>
        </div>
      )}
    </div>
  );
}

function HistoryList({ sessions, onChange }) {
  if (!sessions || sessions.length === 0) {
    return (
      <div style={{ ...card, textAlign: 'center', color: 'var(--grey-500)' }}>
        No sessions logged yet. Pick a workout and tap Log to record your first.
      </div>
    );
  }

  return (
    <>
      <MonthVolume sessions={sessions} />
      {sessions.map(s => <SessionCard key={s.id} s={s} onChange={onChange} />)}
    </>
  );
}

// ── Push opt-in banner ───────────────────────────────────────────────────
// Only shows when notifications are OFF, framed around coach feedback.
// Disappears once enabled. Platform-aware:
//   • Native App Store / Play Store build → Firebase (FCM) permission flow.
//   • Plain browser → web push; on an iPhone Safari tab (no installed app)
//     web push can't work, so we point them to the App Store app instead.
function FeedbackPushBanner() {
  const [state, setState]     = useState('checking'); // checking|hidden|enable|enable_native|denied|get_app|unsupported
  const [busy, setBusy]       = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Native app (Capacitor): use the Firebase permission state.
      if (isNativeApp()) {
        const perm = await getNativePushPermission(); // granted|denied|prompt|unsupported
        if (cancelled) return;
        if (perm === 'granted') setState('hidden');
        else if (perm === 'denied') setState('denied');
        else setState('enable_native');
        return;
      }
      // Plain browser.
      if (!isPushSupported()) {
        // iPhone Safari tab can't do web push — tell them to use the app.
        if (!cancelled) setState(isIOS ? 'get_app' : 'unsupported');
        return;
      }
      const perm = pushPermissionState();
      if (perm === 'denied') { if (!cancelled) setState('denied'); return; }
      if (perm === 'granted') {
        const sub = await getCurrentSubscription().catch(() => null);
        if (!cancelled) setState(sub ? 'hidden' : 'enable');
        return;
      }
      if (!cancelled) setState('enable');
    })();
    return () => { cancelled = true; };
  }, [isIOS]);

  async function handleEnable() {
    setBusy(true);
    const res = isNativeApp() ? await registerNativePush() : await enablePushNotifications();
    setBusy(false);
    if (res.ok) setState('hidden');
    else if (res.reason === 'denied') setState('denied');
    // dismissed/other: leave the banner so they can try again
  }

  if (state === 'checking' || state === 'hidden' || state === 'unsupported' || dismissed) return null;

  const wrap = {
    position: 'relative', background: 'var(--green-pale)', border: '1px solid var(--green)',
    borderRadius: 12, padding: '12px 14px', marginBottom: 16,
  };
  const closeBtn = {
    position: 'absolute', top: 8, right: 10, border: 'none', background: 'none',
    color: 'var(--grey-500)', fontSize: '1rem', lineHeight: 1, cursor: 'pointer',
  };
  const title = { fontWeight: 800, fontSize: '0.9rem', color: 'var(--grey-800, #222)' };
  const body  = { fontSize: '0.82rem', color: 'var(--grey-700)', marginTop: 3, lineHeight: 1.45 };

  return (
    <div style={wrap}>
      <button type="button" aria-label="Dismiss" style={closeBtn} onClick={() => setDismissed(true)}>✕</button>
      {(state === 'enable' || state === 'enable_native') && (
        <>
          <div style={title}>💬 Hear from your coach</div>
          <div style={body}>Turn on notifications to get feedback on your workouts and know when your coach leaves you a note.</div>
          <button type="button" onClick={handleEnable} disabled={busy}
            style={{ marginTop: 10, padding: '9px 16px', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', background: 'var(--green)', color: '#fff', opacity: busy ? 0.6 : 1, cursor: 'pointer' }}>
            {busy ? 'Enabling…' : 'Turn on notifications'}
          </button>
        </>
      )}
      {state === 'get_app' && (
        <>
          <div style={title}>💬 Hear from your coach</div>
          <div style={body}>Get the Team Breakthrough app from the App Store to receive coach feedback and workout notifications on your phone.</div>
        </>
      )}
      {state === 'denied' && (
        <>
          <div style={title}>Notifications are blocked</div>
          <div style={body}>You previously turned notifications off, so you won't get coach feedback alerts. To re-enable, allow notifications for Team Breakthrough in your device settings → Notifications.</div>
        </>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────
export default function TrainingPage() {
  const [tab, setTab] = useState('programmes');
  const [phases, setPhases] = useState([]);
  const [selPhaseId, setSelPhaseId] = useState(null); // which phase is on screen (when there are several)
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [editing, setEditing] = useState(null); // phase object being edited
  const [logging, setLogging] = useState(null); // { phase, programme }

  // Keep the selected phase valid as phases load/change. Default to the first
  // so a member with several blocks lands somewhere sensible, not a blank.
  useEffect(() => {
    if (!phases.length) { setSelPhaseId(null); return; }
    setSelPhaseId(prev => (prev && phases.some(p => p.id === prev)) ? prev : phases[0].id);
  }, [phases]);

  async function loadPhases() {
    const { data } = await supabase.rpc('get_my_training');
    setPhases(data || []);
  }
  async function loadHistory() {
    const { data } = await supabase.rpc('get_my_training_history', { limit_count: 100 });
    setHistory(data || []);
    // Mark any coach feedback as read now that the member has seen it.
    if ((data || []).some(s => s.feedback && !s.feedback.read_at)) {
      supabase.rpc('mark_my_feedback_read').catch(() => {});
    }
  }

  useEffect(() => {
    Promise.all([loadPhases(), loadHistory()]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleDelete(phase) {
    if (!window.confirm(`Remove "${phase.name}"? This hides it and its workouts from your training.`)) return;
    await supabase.rpc('delete_my_phase', { p_phase_id: phase.id });
    loadPhases();
  }

  async function handleDeleteProg(phase, programme) {
    if (!window.confirm(`Delete "${programme.name}" from "${phase.name}"? This can't be undone.`)) return;
    const { data, error } = await supabase.rpc('delete_my_programme', { p_programme_id: programme.id });
    if (error) {
      window.alert(`Couldn't delete "${programme.name}": ${error.message}`);
      return;
    }
    if (data && data.ok === false) {
      window.alert(`"${programme.name}" wasn't deleted — it may already be gone, or it isn't yours to remove.`);
    }
    loadPhases();
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      <h1 className="page-title">My Training</h1>

      <FeedbackPushBanner />

      <a href="#/progress" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '12px 14px', marginBottom: 16, borderRadius: 12, textDecoration: 'none',
        background: 'linear-gradient(135deg, #0D47A1, #1976D2)', color: '#fff',
        boxShadow: 'var(--shadow)',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/images/breakthrough-icon.png" alt="Team Breakthrough" width="34" height="34"
            style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', flexShrink: 0, display: 'block' }} />
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>View Breakthrough progress</span>
        </span>
        <span style={{ fontSize: '1.1rem' }}>›</span>
      </a>

      <div style={{ display: 'flex', background: 'var(--grey-100)', borderRadius: 10, padding: 4, marginBottom: 16 }}>
        {[['programmes', 'Programmes'], ['history', 'History']].map(([key, text]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem',
            background: tab === key ? '#fff' : 'transparent',
            color: tab === key ? 'var(--green)' : 'var(--grey-500)',
            boxShadow: tab === key ? 'var(--shadow)' : 'none',
          }}>{text}</button>
        ))}
      </div>

      {logging && (
        <LogSession phase={logging.phase} programme={logging.programme}
          onCancel={() => setLogging(null)}
          onDone={() => { setLogging(null); loadHistory(); setTab('history'); }} />
      )}

      {!logging && tab === 'programmes' && (
        <>
          {(building || editing) ? (
            <BuildPhase existing={editing}
              onCancel={() => { setBuilding(false); setEditing(null); }}
              onDone={() => { setBuilding(false); setEditing(null); loadPhases(); }} />
          ) : (
            <>
              {phases.length === 0 && (
                <div style={{ ...card, textAlign: 'center', color: 'var(--grey-500)' }}>
                  No programmes yet. Build your own phase, or your trainer can assign one.
                </div>
              )}
              {/* With more than one phase, let the member pick which block to
                  view so the screen doesn't become a long scroll. */}
              {phases.length > 1 && (
                <div style={{ ...card, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--grey-500)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Viewing phase
                  </div>
                  <select
                    value={selPhaseId || ''}
                    onChange={e => setSelPhaseId(e.target.value)}
                    style={{ width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem', fontWeight: 700, color: 'var(--grey-800)' }}>
                    {phases.map(ph => {
                      const n = (ph.programmes || []).length;
                      return (
                        <option key={ph.id} value={ph.id}>
                          {ph.name}{n ? ` · ${n} workout${n === 1 ? '' : 's'}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              {(phases.length > 1 ? phases.filter(ph => ph.id === selPhaseId) : phases).map(ph => (
                <PhaseCard key={ph.id} phase={ph}
                  onLog={(phase, programme) => setLogging({ phase, programme })}
                  onEdit={(phase) => setEditing(phase)}
                  onDelete={handleDelete}
                  onDeleteProg={handleDeleteProg} />
              ))}
              <button className="btn btn-secondary btn-full" style={{ marginTop: 4 }} onClick={() => setBuilding(true)}>
                + Build my own phase
              </button>
            </>
          )}
        </>
      )}

      {!logging && tab === 'history' && <HistoryList sessions={history} onChange={loadHistory} />}
    </div>
  );
}
